import {
  pgTable,
  pgEnum,
  uuid,
  bigint,
  date,
  text,
  integer,
  boolean,
  jsonb,
  timestamp,
  doublePrecision,
  index,
  uniqueIndex,
  unique,
  vector,
  type AnyPgColumn,
  primaryKey,
  foreignKey,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import type { AcquisitionSurveyRecord } from "@/lib/acquisition/survey";
import type { SynopsisDocument } from "@/lib/synopsis";
import type { ChapterDocumentKind } from "@/lib/editor/chapter-versions";

// ─── Enums ───────────────────────────────────────────────

export const userRoleEnum = pgEnum("user_role", ["author", "reader", "admin"]);
export const readerTierEnum = pgEnum("reader_tier", ["basic", "super"]);
export const writerTierEnum = pgEnum("writer_tier", [
  "basic",
  "pro",
  "pro_plus",
]);
export const chapterStatusEnum = pgEnum("chapter_status", [
  "draft",
  "published",
]);
export const accessTierEnum = pgEnum("access_tier", [
  "free",
  "basic",
  "premium",
]);
export const claimRequestStatusEnum = pgEnum("claim_request_status", [
  "pending",
  "approved",
  "rejected",
]);
export const storyVisibilityEnum = pgEnum("story_visibility", [
  "public",
  "private",
]);
export const storyGenreEnum = pgEnum("story_genre", [
  "fantasy",
  "sci_fi",
  "romance",
  "action",
  "litrpg",
  "progression_fantasy",
  "cultivation",
  "isekai",
  "system_apocalypse",
  "dungeon_core",
  "regression",
  "cozy_fantasy",
  "sports",
  "young_adult",
  "thriller",
  "contemporary_romance",
  "romantasy",
  "historical",
  "paranormal",
  "mystery",
  "kingdom_building",
]);
export const storyStatusEnum = pgEnum("story_status", [
  "ongoing",
  "completed",
  "hiatus",
]);
export const wikiCategoryEnum = pgEnum("wiki_category", [
  "characters",
  "locations",
  "systems",
  "factions",
  "organizations",
  "other",
]);
export const coinTransactionTypeEnum = pgEnum("coin_transaction_type", [
  "purchase",
  "chapter_unlock",
  "subscription",
  "refund",
  "cashout",
]);
// Author-side AI credit ledger (SEPARATE from reader coins — coins are a
// reader→author earnings transfer; AI credits are author spend on AI actions).
export const aiCreditTransactionTypeEnum = pgEnum("ai_credit_transaction_type", [
  "grant",
  "purchase",
  "cover_generation",
  "cover_draft",
  "wiki_image_generation",
  "refund",
]);
export const aiCreditOperationStatusEnum = pgEnum("ai_credit_operation_status", [
  "pending",
  "settled",
  "refunded",
]);
export const contactStatusEnum = pgEnum("contact_status", [
  "not_contacted",
  "messaged",
  "responded",
  "claimed",
]);

export const wikiGenerationStatusEnum = pgEnum("wiki_generation_status", [
  "queued",
  "running",
  "succeeded",
  "failed",
]);

export const chapterImportStatusEnum = pgEnum("chapter_import_status", [
  "queued",
  "running",
  "succeeded",
  "failed",
]);

export const fanClubStatusEnum = pgEnum("fan_club_status", [
  "active",
  "paused",
  "archived",
]);

export const fanClubAnnouncementTypeEnum = pgEnum(
  "fan_club_announcement_type",
  ["author", "chapter_release"],
);

// Kinds of notification a user can receive. Add new event types here (e.g.
// "new_follow"), mirror them in NOTIFICATION_TYPE below, and add an icon in the
// exhaustive TYPE_ICON map (src/components/notifications/notification-icons.ts)
// — that Record is typed by NotificationType so it fails to compile until you do.
export const notificationTypeEnum = pgEnum("notification_type", [
  "chapter_comment", // a reader commented on one of the author's chapters
  "chapter_unlock", // a reader spent coins to unlock a premium chapter
  "admin_message", // a platform broadcast from IloriNovel
  "comment_reply", // someone replied to the recipient's chapter comment
  "instagram_post_ready", // an admin uploaded an Instagram post for author review
  "instagram_review_comment", // an author/admin replied in an Instagram review thread
  "instagram_post_approved", // an author approved an Instagram post
  "fan_club_announcement", // an author posted to a joined story's fan club
  "fan_club_chapter_release", // a joined story published a new chapter
]);

export const payoutMethodEnum = pgEnum("payout_method", ["venmo", "paypal"]);

// Author referral lifecycle. `pending` = referred author signed up but hasn't
// yet met the qualification bar; `qualified` = met the bar, payout owed;
// `paid` = referrer has been paid out; `expired` = the 14-day window closed
// without the referred author sustaining publishing.
export const referralStatusEnum = pgEnum("referral_status", [
  "pending",
  "qualified",
  "paid",
  "expired",
]);

export const socialPlatformEnum = pgEnum("social_platform", [
  "tiktok",
  "instagram",
]);

export const socialPostKindEnum = pgEnum("social_post_kind", [
  "video",
  "image",
  "carousel",
]);

export const socialPostStatusEnum = pgEnum("social_post_status", [
  "processing", // dispatched to the platform, awaiting terminal state
  "sent_to_inbox", // TikTok inbox flow: delivered; author finishes in the app
  "published", // live on the platform
  "failed",
]);

export const authorInstagramReviewStatusEnum = pgEnum(
  "author_instagram_review_status",
  ["pending", "approved", "changes_requested"]
);

// ─── Enum constants ──────────────────────────────────────
// Single source of truth for DB enum values. `satisfies` keeps each constant
// in sync with its pgEnum — adding a new value to the pgEnum without updating
// the constant below is a compile error.

export const CHAPTER_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
} as const satisfies Record<string, typeof chapterStatusEnum.enumValues[number]>;
export type ChapterStatus = typeof chapterStatusEnum.enumValues[number];

export const ACCESS_TIER = {
  FREE: "free",
  BASIC: "basic",
  PREMIUM: "premium",
} as const satisfies Record<string, typeof accessTierEnum.enumValues[number]>;
export type AccessTier = typeof accessTierEnum.enumValues[number];

export const SOCIAL_PLATFORM = {
  TIKTOK: "tiktok",
  INSTAGRAM: "instagram",
} as const satisfies Record<string, typeof socialPlatformEnum.enumValues[number]>;
export type SocialPlatform = typeof socialPlatformEnum.enumValues[number];

export const SOCIAL_POST_KIND = {
  VIDEO: "video",
  IMAGE: "image",
  CAROUSEL: "carousel",
} as const satisfies Record<string, typeof socialPostKindEnum.enumValues[number]>;
export type SocialPostKind = typeof socialPostKindEnum.enumValues[number];

export const SOCIAL_POST_STATUS = {
  PROCESSING: "processing",
  SENT_TO_INBOX: "sent_to_inbox",
  PUBLISHED: "published",
  FAILED: "failed",
} as const satisfies Record<string, typeof socialPostStatusEnum.enumValues[number]>;
export type SocialPostStatus = typeof socialPostStatusEnum.enumValues[number];

export const AUTHOR_INSTAGRAM_REVIEW_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  CHANGES_REQUESTED: "changes_requested",
} as const satisfies Record<
  string,
  typeof authorInstagramReviewStatusEnum.enumValues[number]
>;
export type AuthorInstagramReviewStatus =
  typeof authorInstagramReviewStatusEnum.enumValues[number];

export const USER_ROLE = {
  AUTHOR: "author",
  READER: "reader",
  ADMIN: "admin",
} as const satisfies Record<string, typeof userRoleEnum.enumValues[number]>;
export type UserRole = typeof userRoleEnum.enumValues[number];

export const STORY_VISIBILITY = {
  PUBLIC: "public",
  PRIVATE: "private",
} as const satisfies Record<string, typeof storyVisibilityEnum.enumValues[number]>;
export type StoryVisibility = typeof storyVisibilityEnum.enumValues[number];

export const READER_TIER = {
  BASIC: "basic",
  SUPER: "super",
} as const satisfies Record<string, typeof readerTierEnum.enumValues[number]>;
export type ReaderTier = typeof readerTierEnum.enumValues[number];

export const CLAIM_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const satisfies Record<string, typeof claimRequestStatusEnum.enumValues[number]>;
export type ClaimStatus = typeof claimRequestStatusEnum.enumValues[number];

export const STORY_STATUS = {
  ONGOING: "ongoing",
  COMPLETED: "completed",
  HIATUS: "hiatus",
} as const satisfies Record<string, typeof storyStatusEnum.enumValues[number]>;
export type StoryStatus = typeof storyStatusEnum.enumValues[number];

export const WIKI_CATEGORY = {
  CHARACTERS: "characters",
  LOCATIONS: "locations",
  SYSTEMS: "systems",
  FACTIONS: "factions",
  ORGANIZATIONS: "organizations",
  OTHER: "other",
} as const satisfies Record<string, typeof wikiCategoryEnum.enumValues[number]>;
export type LoreCategory = typeof wikiCategoryEnum.enumValues[number];

export const COIN_TX_TYPE = {
  PURCHASE: "purchase",
  CHAPTER_UNLOCK: "chapter_unlock",
  SUBSCRIPTION: "subscription",
  REFUND: "refund",
  CASHOUT: "cashout",
} as const satisfies Record<string, typeof coinTransactionTypeEnum.enumValues[number]>;
export type CoinTxType = typeof coinTransactionTypeEnum.enumValues[number];

export const AI_CREDIT_TX_TYPE = {
  GRANT: "grant",
  PURCHASE: "purchase",
  COVER_GENERATION: "cover_generation",
  COVER_DRAFT: "cover_draft",
  WIKI_IMAGE_GENERATION: "wiki_image_generation",
  REFUND: "refund",
} as const satisfies Record<string, typeof aiCreditTransactionTypeEnum.enumValues[number]>;
export type AiCreditTxType = typeof aiCreditTransactionTypeEnum.enumValues[number];

export const AI_CREDIT_OP_STATUS = {
  PENDING: "pending",
  SETTLED: "settled",
  REFUNDED: "refunded",
} as const satisfies Record<string, typeof aiCreditOperationStatusEnum.enumValues[number]>;
export type AiCreditOpStatus = typeof aiCreditOperationStatusEnum.enumValues[number];

export const CONTACT_STATUS = {
  NOT_CONTACTED: "not_contacted",
  MESSAGED: "messaged",
  RESPONDED: "responded",
  CLAIMED: "claimed",
} as const satisfies Record<string, typeof contactStatusEnum.enumValues[number]>;
export type ContactStatus = typeof contactStatusEnum.enumValues[number];

export const FAN_CLUB_STATUS = {
  ACTIVE: "active",
  PAUSED: "paused",
  ARCHIVED: "archived",
} as const satisfies Record<string, typeof fanClubStatusEnum.enumValues[number]>;
export type FanClubStatus = typeof fanClubStatusEnum.enumValues[number];

export const FAN_CLUB_ANNOUNCEMENT_TYPE = {
  AUTHOR: "author",
  CHAPTER_RELEASE: "chapter_release",
} as const satisfies Record<
  string,
  typeof fanClubAnnouncementTypeEnum.enumValues[number]
>;
export type FanClubAnnouncementType =
  typeof fanClubAnnouncementTypeEnum.enumValues[number];

export const NOTIFICATION_TYPE = {
  CHAPTER_COMMENT: "chapter_comment",
  CHAPTER_UNLOCK: "chapter_unlock",
  ADMIN_MESSAGE: "admin_message",
  COMMENT_REPLY: "comment_reply",
  INSTAGRAM_POST_READY: "instagram_post_ready",
  INSTAGRAM_REVIEW_COMMENT: "instagram_review_comment",
  INSTAGRAM_POST_APPROVED: "instagram_post_approved",
  FAN_CLUB_ANNOUNCEMENT: "fan_club_announcement",
  FAN_CLUB_CHAPTER_RELEASE: "fan_club_chapter_release",
} as const satisfies Record<string, typeof notificationTypeEnum.enumValues[number]>;
export type NotificationType = typeof notificationTypeEnum.enumValues[number];

export const PAYOUT_METHOD = {
  VENMO: "venmo",
  PAYPAL: "paypal",
} as const satisfies Record<string, typeof payoutMethodEnum.enumValues[number]>;
export type PayoutMethod = typeof payoutMethodEnum.enumValues[number];

export const REFERRAL_STATUS = {
  PENDING: "pending",
  QUALIFIED: "qualified",
  PAID: "paid",
  EXPIRED: "expired",
} as const satisfies Record<string, typeof referralStatusEnum.enumValues[number]>;
export type ReferralStatus = typeof referralStatusEnum.enumValues[number];

// Author referral program economics — single source of truth.
export const REFERRAL_PAYOUT_CENTS = 2500; // $25 per qualified referral
export const REFERRAL_CAP_CENTS = 25000; // $250 lifetime cap per referrer
export const REFERRAL_MAX_PAID = REFERRAL_CAP_CENTS / REFERRAL_PAYOUT_CENTS; // 10

// ─── Profiles ────────────────────────────────────────────

export const profiles = pgTable("profiles", {
  id: uuid("id").primaryKey(),
  displayName: text("display_name").notNull(),
  // OAuth providers supply a real name automatically, but people should
  // explicitly choose whether that is the public pen name they want to use.
  // Native signups already make that choice in the signup form.
  hasConfirmedPenName: boolean("has_confirmed_pen_name").notNull().default(true),
  authorSlug: text("author_slug").unique(),
  // DEPRECATED author/reader identity — being replaced by `isAuthor` (author
  // capability) + `user_roles` (admin). Still dual-written this release for
  // rollback safety; slated to be dropped once prod is verified. Never read it
  // for new code — use `isAuthor` (or `is_author OR admin` for author-access).
  role: userRoleEnum("role").notNull().default("reader"),
  // Author capability. Base users are readers (false); `true` grants the author
  // workspace. Admin is separate (see `user_roles`); author-access is
  // `isAuthor OR admin`. Tiers (readerTier/writerTier) are an independent axis.
  isAuthor: boolean("is_author").notNull().default(false),
  readerTier: readerTierEnum("reader_tier").notNull().default("basic"),
  writerTier: writerTierEnum("writer_tier").default("basic"),
  avatarUrl: text("avatar_url"),
  bio: text("bio"),
  stripeAccountId: text("stripe_account_id"),
  stripeCustomerId: text("stripe_customer_id"),
  themePreference: text("theme_preference").default("system"),
  // Author preference for the floating dictation control. Existing and new
  // profiles default on; authors can hide it in-editor and restore it later.
  showDictationTool: boolean("show_dictation_tool").notNull().default(true),
  // Null preserves the private-beta grant behavior until an author makes an
  // explicit choice. Once set, the author's Flare preference wins over any
  // historical beta grant. The global feature switch remains authoritative.
  assistantFlareEnabled: boolean("assistant_flare_enabled"),
  // Notification preferences. `notifyInApp` gates rows in the bell/feed, while
  // `notifyEmail` independently gates transactional notification emails,
  // including admin broadcasts. Both default on.
  notifyInApp: boolean("notify_in_app").notNull().default(true),
  notifyEmail: boolean("notify_email").notNull().default(true),
  archivedAt: timestamp("archived_at", { withTimezone: true }),
  // Null = profile was seeded by import/admin and the real author hasn't taken
  // it over yet. Set on successful claim. Used to gate the "Claim this profile"
  // CTA and to measure claim-conversion for the author-acquisition funnel.
  claimedAt: timestamp("claimed_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  // Outreach tracking — populated manually or by admin tooling when researching
  // contact channels for unclaimed seeded authors.
  socialLinks: jsonb("social_links").$type<{
    royal_road?: string;
    patreon?: string;
    discord?: string;
    website?: string;
    reddit?: string;
    twitter?: string;
    spacebattles?: string;
  }>().default(sql`'{}'::jsonb`),
  contactStatus: contactStatusEnum("contact_status").default("not_contacted"),
  contactNotes: text("contact_notes"),
  // Author referral program. `referralCode` is this author's own shareable code
  // (generated lazily on first visit to the referral dashboard). Payout fields
  // are how the referrer wants to be paid their $25/qualified-referral bounty.
  referralCode: text("referral_code").unique(),
  payoutMethod: payoutMethodEnum("payout_method"),
  payoutHandle: text("payout_handle"),
  // First-touch acquisition attribution, captured at signup from the
  // `iloris_attribution` cookie that middleware stamps on a visitor's first external
  // landing (see `lib/attribution/`). Answers channel-ROI questions ("how many
  // authors came from Reddit?") directly in SQL, without leaning on PostHog
  // identity-stitching or fragile UTM query-strings that mobile apps strip.
  // Null = direct/unknown. Query e.g. `acquisition->>'utm_source' = 'reddit'`.
  acquisitionSurvey: jsonb("acquisition_survey").$type<AcquisitionSurveyRecord>(),
  acquisition: jsonb("acquisition").$type<{
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
    referrer?: string;
    landing_page?: string;
    rdt_cid?: string;
    first_seen_at?: string;
  }>(),
}, (table) => [
  uniqueIndex("profiles_pen_name_unique").on(sql`lower(btrim(${table.displayName}))`),
]);

// ─── User Roles (junction) ───────────────────────────────
// Admin capability store. Only `admin` rows live here now — author/reader are
// no longer mirrored (author is `profiles.is_author`; reader is the base
// state). Kept as a locked, service-role-only table so admin can never be
// self-granted via the user-editable profile row.

export const userRoles = pgTable(
  "user_roles",
  {
    userId: uuid("user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    role: userRoleEnum("role").notNull(),
  },
  (table) => [primaryKey({ columns: [table.userId, table.role] })]
);

// ─── Account Moderation ─────────────────────────────────
// Moderation state is intentionally separate from profiles. Profile owners can
// edit their public profile, while only privileged server-side moderation code
// may change this locked projection. `profiles.archived_at` remains the public
// visibility/lifecycle projection and is updated atomically with this row.

export const userModerationStatuses = pgTable(
  "user_moderation_statuses",
  {
    userId: uuid("user_id")
      .primaryKey()
      .references(() => profiles.id, { onDelete: "cascade" }),
    status: text("status").notNull(),
    strikeCount: integer("strike_count").notNull(),
    version: integer("version").notNull(),
    archiveOwnedByModeration: boolean("archive_owned_by_moderation")
      .notNull()
      .default(false),
    restrictionStartedAt: timestamp("restriction_started_at", {
      withTimezone: true,
    }),
    bannedAt: timestamp("banned_at", { withTimezone: true }),
    authBanSyncedAt: timestamp("auth_ban_synced_at", { withTimezone: true }),
    authBanSyncErrorCode: text("auth_ban_sync_error_code"),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    index("user_moderation_statuses_status_updated_idx").on(
      table.status,
      table.updatedAt,
    ),
    check(
      "user_moderation_statuses_status_check",
      sql`${table.status} in ('active', 'strike_archived', 'banned')`,
    ),
    check(
      "user_moderation_statuses_counter_check",
      sql`${table.strikeCount} >= 1 and ${table.version} >= 1`,
    ),
    check(
      "user_moderation_statuses_shape_check",
      sql`(
        ${table.status} = 'active'
        and ${table.restrictionStartedAt} is null
        and ${table.bannedAt} is null
        and not ${table.archiveOwnedByModeration}
      ) or (
        ${table.status} = 'strike_archived'
        and ${table.restrictionStartedAt} is not null
        and ${table.bannedAt} is null
      ) or (
        ${table.status} = 'banned'
        and ${table.strikeCount} >= 2
        and ${table.restrictionStartedAt} is not null
        and ${table.bannedAt} is not null
      )`,
    ),
    check(
      "user_moderation_statuses_auth_sync_check",
      sql`${table.authBanSyncedAt} is null or ${table.status} = 'banned'`,
    ),
  ],
);

// Append-only action ledger. Actor and target UUIDs are snapshots rather than
// foreign keys so deleting an account cannot erase moderation accountability.
export const accountModerationEvents = pgTable(
  "account_moderation_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    targetUserId: uuid("target_user_id").notNull(),
    actorUserId: uuid("actor_user_id").notNull(),
    action: text("action").notNull(),
    previousStatus: text("previous_status").notNull(),
    newStatus: text("new_status").notNull(),
    previousStrikeCount: integer("previous_strike_count").notNull(),
    newStrikeCount: integer("new_strike_count").notNull(),
    reasonCode: text("reason_code").notNull(),
    reasonNote: text("reason_note").notNull(),
    idempotencyKey: uuid("idempotency_key").notNull().unique(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    index("account_moderation_events_target_created_idx").on(
      table.targetUserId,
      table.createdAt,
    ),
    index("account_moderation_events_actor_created_idx").on(
      table.actorUserId,
      table.createdAt,
    ),
    check(
      "account_moderation_events_action_check",
      sql`${table.action} in ('strike_issued', 'restriction_lifted')`,
    ),
    check(
      "account_moderation_events_status_check",
      sql`${table.previousStatus} in ('active', 'strike_archived', 'banned')
        and ${table.newStatus} in ('active', 'strike_archived', 'banned')`,
    ),
    check(
      "account_moderation_events_strike_count_check",
      sql`${table.previousStrikeCount} >= 0
        and ${table.newStrikeCount} >= ${table.previousStrikeCount}`,
    ),
    check(
      "account_moderation_events_reason_code_check",
      sql`${table.reasonCode} in ('spam', 'harassment', 'impersonation', 'fraud', 'unsafe_content', 'terms_violation', 'other')`,
    ),
    check(
      "account_moderation_events_reason_note_check",
      sql`char_length(${table.reasonNote}) between 3 and 500`,
    ),
  ],
);

// ─── Feature Entitlements ────────────────────────────────
// Feature definitions provide a global kill switch. Grants allow specific
// users to access a feature without coupling rollout policy to pricing tiers
// or the admin-only user_roles table.

export const features = pgTable("features", {
  id: uuid("id").primaryKey().defaultRandom(),
  key: text("key").notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),
  enabled: boolean("enabled").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date())
    .notNull(),
});

export const userFeatureGrants = pgTable(
  "user_feature_grants",
  {
    userId: uuid("user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    featureId: uuid("feature_id")
      .notNull()
      .references(() => features.id, { onDelete: "cascade" }),
    grantedBy: uuid("granted_by").references(() => profiles.id, {
      onDelete: "set null",
    }),
    grantedAt: timestamp("granted_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
  },
  (table) => [
    primaryKey({ columns: [table.userId, table.featureId] }),
    index("user_feature_grants_feature_id_idx").on(table.featureId),
  ],
);

// Append-only entitlement history. User and actor IDs intentionally remain
// snapshots rather than foreign keys so account deletion cannot erase the
// administrative record.
export const featureGrantAuditEvents = pgTable(
  "feature_grant_audit_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull(),
    featureKey: text("feature_key").notNull(),
    action: text("action").notNull(),
    actorUserId: uuid("actor_user_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("feature_grant_audit_events_user_id_idx").on(table.userId),
    index("feature_grant_audit_events_feature_key_idx").on(table.featureKey),
    check(
      "feature_grant_audit_events_action_check",
      sql`${table.action} in ('granted', 'revoked')`,
    ),
  ],
);

// Append-only history for global feature availability changes. Actor IDs and
// feature keys are snapshots rather than foreign keys so deleting an account
// or feature definition cannot erase who changed an emergency control.
export const featureToggleAuditEvents = pgTable(
  "feature_toggle_audit_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    featureKey: text("feature_key").notNull(),
    previousEnabled: boolean("previous_enabled").notNull(),
    enabled: boolean("enabled").notNull(),
    actorUserId: uuid("actor_user_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("feature_toggle_audit_events_feature_key_created_at_idx").on(
      table.featureKey,
      table.createdAt,
    ),
    check(
      "feature_toggle_audit_events_state_change_check",
      sql`${table.previousEnabled} <> ${table.enabled}`,
    ),
  ],
);

// ─── Referrals ───────────────────────────────────────────
// Private, constant-size evidence for paid-referral access. No manuscript text.
export const authorReferralActivity = pgTable("author_referral_activity", {
  authorId: uuid("author_id").notNull().references(() => profiles.id, { onDelete: "cascade" }),
  policyVersion: integer("policy_version").notNull(),
  firstWritingAt: timestamp("first_writing_at", { withTimezone: true }).notNull(),
  lastCreditedWritingAt: timestamp("last_credited_writing_at", { withTimezone: true }).notNull(),
  writingDayCount: integer("writing_day_count").notNull(),
  eligibleAfter: timestamp("eligible_after", { withTimezone: true }),
}, (table) => [
  primaryKey({ columns: [table.authorId, table.policyVersion] }),
  // This schema is also imported by clients; keep exact policy thresholds in the private recorder.
  check("referral_activity_day_count", sql`${table.writingDayCount} >= 1`),
  check("referral_activity_time_order", sql`${table.lastCreditedWritingAt} >= ${table.firstWritingAt}`),
  check("referral_activity_ready", sql`${table.eligibleAfter} is null or ${table.eligibleAfter} >= ${table.firstWritingAt}`),
]).enableRLS();

// One row per referred author. `referrerId` earns the bounty; `referredId` is
// the newly-signed-up author who must publish 3 chapters and keep publishing
// through the following fortnight to make the referral `qualified` (payable).
// `referredId` is unique — a person can only ever be referred once.

export const referrals = pgTable(
  "referrals",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    referrerId: uuid("referrer_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    referredId: uuid("referred_id")
      .notNull()
      .unique()
      .references(() => profiles.id, { onDelete: "cascade" }),
    // The referral code used at signup — snapshotted for auditing even if the
    // referrer later regenerates their code.
    code: text("code").notNull(),
    status: referralStatusEnum("status").notNull().default("pending"),
    // Timestamp the referred author published their 3rd chapter — starts the
    // 14-day sustained-publishing clock. Null until they reach 3.
    thirdChapterAt: timestamp("third_chapter_at", { withTimezone: true }),
    qualifiedAt: timestamp("qualified_at", { withTimezone: true }),
    paidAt: timestamp("paid_at", { withTimezone: true }),
    payoutAmountCents: integer("payout_amount_cents")
      .notNull()
      .default(2500),
    // Payout snapshot taken when an admin marks the referral paid.
    payoutMethod: payoutMethodEnum("payout_method"),
    payoutHandle: text("payout_handle"),
    payoutReference: text("payout_reference"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("referrals_referrer_id_idx").on(table.referrerId),
    index("referrals_status_idx").on(table.status),
  ]
);

// ─── Stories ─────────────────────────────────────────────

export const stories = pgTable("stories", {
  id: uuid("id").primaryKey().defaultRandom(),
  authorId: uuid("author_id")
    .notNull()
    .references(() => profiles.id),
  title: text("title").notNull(),
  slug: text("slug").notNull().unique(),
  synopsis: text("synopsis"),
  synopsisContent: jsonb("synopsis_content").$type<SynopsisDocument>(),
  // Controls reader UI only; generated audio remains available to the TTS service.
  showTextToSpeech: boolean("show_text_to_speech").notNull().default(true),
  isMature: boolean("is_mature").notNull().default(false),
  coverUrl: text("cover_url"),
  externalCtaEnabled: boolean("external_cta_enabled").notNull().default(false),
  externalCtaMessage: text("external_cta_message"),
  externalCtaUrl: text("external_cta_url"),
  externalCtaButtonLabel: text("external_cta_button_label"),
  visibility: storyVisibilityEnum("visibility").notNull().default("private"),
  published: boolean("published").notNull().default(false),
  // Editorial "featured" flag — admin-only. Drives the reader discovery hero.
  featured: boolean("featured").notNull().default(false),
  status: storyStatusEnum("status").notNull().default("ongoing"),
  outlineVersion: integer("outline_version").notNull().default(1),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date()),
},
  (table) => [
    index("stories_author_id_idx").on(table.authorId),
    check(
      "stories_synopsis_content_size_check",
      sql`${table.synopsisContent} is null or octet_length(${table.synopsisContent}::text) <= 256000`,
    ),
    check(
      "stories_external_cta_message_length_check",
      sql`${table.externalCtaMessage} is null or char_length(${table.externalCtaMessage}) <= 500`,
    ),
    check(
      "stories_external_cta_url_length_check",
      sql`${table.externalCtaUrl} is null or char_length(${table.externalCtaUrl}) <= 2048`,
    ),
    check(
      "stories_external_cta_url_scheme_check",
      sql`${table.externalCtaUrl} is null or ${table.externalCtaUrl} ~* '^https?://'`,
    ),
    check(
      "stories_external_cta_button_label_length_check",
      sql`${table.externalCtaButtonLabel} is null or char_length(${table.externalCtaButtonLabel}) <= 50`,
    ),
    check(
      "stories_external_cta_enabled_fields_check",
      sql`not ${table.externalCtaEnabled} or (
        nullif(btrim(${table.externalCtaMessage}), '') is not null
        and nullif(btrim(${table.externalCtaUrl}), '') is not null
      )`,
    ),
  ]
);

// ─── Author Promotion Enrollments ───────────────────────
// Participation is explicit and campaign-scoped. Immutable eligibility facts
// live in `chapterFirstPublicationFacts`; this row records who was approved,
// which single story counts, and the exact versioned challenge terms.
export const authorPromotionEnrollments = pgTable(
  "author_promotion_enrollments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    campaignKey: text("campaign_key").notNull(),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }).notNull(),
    termsVersion: text("terms_version").notNull().default("author-challenge-2026-v3"),
    payoutCents: integer("payout_cents").notNull().default(3000),
    minimumWordCount: integer("minimum_word_count").notNull().default(1000),
    requiredWindows: integer("required_windows").notNull().default(12),
    windowDays: integer("window_days").notNull().default(7),
    chaptersPerWindow: integer("chapters_per_window").notNull().default(1),
    status: text("status").notNull().default("active"),
    createdBy: uuid("created_by").references(() => profiles.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => [
    unique("author_promotion_enrollments_campaign_author_unique").on(
      table.campaignKey,
      table.authorId,
    ),
    unique("author_promotion_enrollments_campaign_story_unique").on(
      table.campaignKey,
      table.storyId,
    ),
    index("author_promotion_enrollments_campaign_status_idx").on(
      table.campaignKey,
      table.status,
    ),
    index("author_promotion_enrollments_story_status_idx").on(
      table.storyId,
      table.status,
    ),
    check(
      "author_promotion_enrollments_status_check",
      sql`${table.status} in ('active', 'cancelled', 'paid')`,
    ),
    check(
      "author_promotion_enrollments_window_check",
      sql`${table.endsAt} > ${table.startsAt}`,
    ),
    check("author_promotion_enrollments_payout_check", sql`${table.payoutCents} > 0`),
    check("author_promotion_enrollments_terms_check", sql`${table.minimumWordCount} > 0 and ${table.requiredWindows} > 0 and ${table.windowDays} > 0 and ${table.chaptersPerWindow} > 0`),
  ],
);

// Immutable publication-time evidence. IDs are deliberate snapshots rather
// than foreign keys so later chapter/story/account deletion cannot erase the
// facts used to make a payout decision.
export const chapterFirstPublicationFacts = pgTable(
  "chapter_first_publication_facts",
  {
    chapterId: uuid("chapter_id").primaryKey(),
    storyId: uuid("story_id").notNull(),
    authorId: uuid("author_id").notNull(),
    firstPublishedAt: timestamp("first_published_at", { withTimezone: true }).notNull(),
    wordCount: integer("word_count").notNull(),
    storyWasPublic: boolean("story_was_public").notNull(),
    source: text("source").notNull().default("live"),
    capturedAt: timestamp("captured_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    index("chapter_first_publication_facts_story_published_idx").on(
      table.storyId,
      table.firstPublishedAt,
    ),
    check("chapter_first_publication_facts_word_count_check", sql`${table.wordCount} >= 0`),
    check("chapter_first_publication_facts_source_check", sql`${table.source} in ('live', 'backfill')`),
  ],
);

// Append-only moment when an enrolled chapter first satisfies that enrollment's
// word threshold while published on a public story. A chapter may reach the
// threshold after first publication; it counts in the window containing this
// timestamp, never retroactively in its original publication window.
export const authorPromotionQualificationFacts = pgTable(
  "author_promotion_qualification_facts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    enrollmentId: uuid("enrollment_id").notNull(),
    campaignKey: text("campaign_key").notNull(),
    chapterId: uuid("chapter_id").notNull(),
    storyId: uuid("story_id").notNull(),
    authorId: uuid("author_id").notNull(),
    qualifiedAt: timestamp("qualified_at", { withTimezone: true }).notNull(),
    wordCount: integer("word_count").notNull(),
    source: text("source").notNull().default("live"),
    capturedAt: timestamp("captured_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    unique("author_promotion_qualification_enrollment_chapter_unique").on(
      table.enrollmentId,
      table.chapterId,
    ),
    index("author_promotion_qualification_enrollment_time_idx").on(
      table.enrollmentId,
      table.qualifiedAt,
    ),
    check("author_promotion_qualification_word_count_check", sql`${table.wordCount} > 0`),
    check("author_promotion_qualification_source_check", sql`${table.source} in ('live', 'backfill')`),
  ],
);

// Append-only payout receipt and eligibility snapshot. This is separate from
// the mutable enrollment lifecycle so a paid decision cannot be rewritten.
export const authorPromotionPayouts = pgTable(
  "author_promotion_payouts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    enrollmentId: uuid("enrollment_id")
      .notNull()
      .unique(),
    campaignKey: text("campaign_key").notNull(),
    authorId: uuid("author_id").notNull(),
    authorName: text("author_name").notNull(),
    storyId: uuid("story_id").notNull(),
    storyTitle: text("story_title").notNull(),
    amountCents: integer("amount_cents").notNull(),
    termsVersion: text("terms_version").notNull(),
    eligibilitySnapshot: jsonb("eligibility_snapshot").notNull(),
    paidBy: uuid("paid_by").notNull(),
    paymentReference: text("payment_reference").notNull(),
    paidAt: timestamp("paid_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    index("author_promotion_payouts_author_id_idx").on(table.authorId),
    check("author_promotion_payouts_amount_check", sql`${table.amountCents} > 0`),
  ],
);

// ─── Author Story Outline ────────────────────────────────

export const storyVolumes = pgTable(
  "story_volumes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    title: text("title").notNull(),
    sortOrder: integer("sort_order").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    unique("story_volumes_id_story_id_unique").on(table.id, table.storyId),
    unique("story_volumes_story_id_sort_order_unique").on(
      table.storyId,
      table.sortOrder,
    ),
    index("story_volumes_story_id_idx").on(table.storyId),
  ],
);

// ─── Story Genres (many-to-many) ─────────────────────────

export const storyGenres = pgTable(
  "story_genres",
  {
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    genre: storyGenreEnum("genre").notNull(),
  },
  (table) => [
    primaryKey({ columns: [table.storyId, table.genre] }),
  ]
);

// ─── Tags (many-to-many) ─────────────────────────────────

export const tags = pgTable("tags", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull().unique(),
  label: text("label").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const storyTags = pgTable(
  "story_tags",
  {
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    tagId: uuid("tag_id")
      .notNull()
      .references(() => tags.id, { onDelete: "cascade" }),
  },
  (table) => [
    primaryKey({ columns: [table.storyId, table.tagId] }),
  ]
);

// ─── Chapters ────────────────────────────────────────────

export const chapters = pgTable("chapters", {
  id: uuid("id").primaryKey().defaultRandom(),
  storyId: uuid("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  volumeId: uuid("volume_id").notNull(),
  title: text("title").notNull(),
  sortOrder: integer("sort_order").notNull(),
  content: jsonb("content").notNull().default({}),
  // Optional end-of-chapter author's note (TipTap JSON). Editable post-publish
  // without touching publish state.
  authorNote: jsonb("author_note").$type<Record<string, unknown>>(),
  wordCount: integer("word_count").default(0),
  hasAudio: boolean("has_audio").notNull().default(false),
  accessTier: accessTierEnum("access_tier").notNull().default("free"),
  status: chapterStatusEnum("status").notNull().default("draft"),
  publishedAt: timestamp("published_at", { withTimezone: true }),
  scheduledReleaseAt: timestamp("scheduled_release_at", { withTimezone: true }),
  releaseTimeZone: text("release_time_zone"),
  releaseVersion: integer("release_version").notNull().default(0),
  // Immutable first-publish fact used for author consistency and featuring.
  // Unlike publishedAt, this is never cleared by an unpublish/re-publish cycle.
  firstPublishedAt: timestamp("first_published_at", { withTimezone: true }),
  // Lightweight durable revision facts. Content itself never enters analytics.
  publishedRevisionCount: integer("published_revision_count").notNull().default(0),
  lastPublishedRevisionAt: timestamp("last_published_revision_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date()),
},
  (table) => [
    foreignKey({
      columns: [table.volumeId, table.storyId],
      foreignColumns: [storyVolumes.id, storyVolumes.storyId],
      name: "chapters_volume_story_fk",
    }).onDelete("restrict"),
    // Reader/browse hot path: fetch a story's chapters in order. Replaces the
    // sequential scan on this 213 MB table (advisor: unindexed FK chapters.story_id).
    index("chapters_story_id_sort_order_idx").on(table.storyId, table.sortOrder),
    index("chapters_volume_id_sort_order_idx").on(table.volumeId, table.sortOrder),
    index("chapters_due_release_idx").on(table.scheduledReleaseAt, table.storyId)
      .where(sql`${table.status} = 'draft' and ${table.scheduledReleaseAt} is not null`),
    check("chapters_release_shape", sql`(${table.scheduledReleaseAt} is null and ${table.releaseTimeZone} is null) or (${table.status} = 'draft' and ${table.scheduledReleaseAt} is not null and ${table.releaseTimeZone} is not null and extract(epoch from ${table.scheduledReleaseAt})::numeric % 3600 = 0)`),
  ]
).enableRLS();

// Publication commits before external work. Pending rows survive crashes and
// QStash retries; only delete them after cache invalidation and index dispatch.
export const chapterReleaseOutbox = pgTable("chapter_release_outbox", {
  id: uuid("id").primaryKey().defaultRandom(),
  storyId: uuid("story_id").notNull().references(() => stories.id, { onDelete: "cascade" }),
  authorId: uuid("author_id").notNull().references(() => profiles.id, { onDelete: "cascade" }),
  publications: jsonb("publications").notNull().$type<{ chapterId: string; wordCount: number | null }[]>(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  attempts: integer("attempts").notNull().default(0),
  lastError: text("last_error"),
  nextAttemptAt: timestamp("next_attempt_at", { withTimezone: true }).notNull().defaultNow(),
  quarantinedAt: timestamp("quarantined_at", { withTimezone: true }),
}, table => [
  index("chapter_release_outbox_created_idx").on(table.createdAt),
  index("chapter_release_outbox_ready_idx").on(table.nextAttemptAt, table.createdAt).where(sql`${table.quarantinedAt} is null`),
]).enableRLS();

// Private working copies are deliberately separate from publicly readable
// chapters. Publishing copies only the manuscript into the canonical chapter row.
export const chapterVersions = pgTable("chapter_versions", {
  id: uuid("id").primaryKey().defaultRandom(),
  chapterId: uuid("chapter_id")
    .notNull()
    .references(() => chapters.id, { onDelete: "cascade" }),
  kind: text("kind").$type<ChapterDocumentKind>().notNull(),
  content: jsonb("content").$type<Record<string, unknown>>(),
  wordCount: integer("word_count").notNull().default(0),
  lastPublishedAt: timestamp("last_published_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow()
    .$onUpdateFn(() => new Date()),
}, (table) => [
  unique("chapter_versions_chapter_kind_unique").on(table.chapterId, table.kind),
  check("chapter_versions_kind_check", sql`${table.kind} in ('notes', 'manuscript')`),
  check("chapter_versions_word_count_check", sql`${table.wordCount} >= 0`),
]);

// ─── Story Fan Clubs ────────────────────────────────────
// A club is story-scoped. Its display name is deliberately derived from the
// current story title rather than duplicated here, so story renames cannot
// leave stale community branding behind.
export const fanClubs = pgTable(
  "fan_clubs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    storyId: uuid("story_id")
      .notNull()
      .unique()
      .references(() => stories.id, { onDelete: "cascade" }),
    // Denormalized for efficient author scoping and RLS. The transfer trigger
    // keeps this aligned with stories.author_id while pausing the club.
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    description: text("description"),
    status: fanClubStatusEnum("status").notNull().default("paused"),
    autoAnnounceChapterReleases: boolean("auto_announce_chapter_releases")
      .notNull()
      .default(true),
    // Monotonic admission token for Discord OAuth callbacks. Every new OAuth
    // attempt and explicit disconnect advances it, so an older callback can
    // never recreate a connection after the author revoked it.
    discordConnectionGeneration: bigint("discord_connection_generation", {
      mode: "number",
    })
      .notNull()
      .default(0),
    // Delivery revocation epoch. Metadata edits continue to update updated_at,
    // while only lifecycle changes advance this cutoff.
    deliveryEpochAt: timestamp("delivery_epoch_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    unique("fan_clubs_id_story_unique").on(table.id, table.storyId),
    index("fan_clubs_author_id_idx").on(table.authorId),
    index("fan_clubs_status_idx").on(table.status),
    check(
      "fan_clubs_description_length_check",
      sql`${table.description} is null or char_length(${table.description}) <= 600`,
    ),
    check(
      "fan_clubs_discord_connection_generation_check",
      sql`${table.discordConnectionGeneration} >= 0`,
    ),
  ],
);

// Explicit reader membership is owned by Iloris. It is intentionally separate
// from Discord membership, which an invite click cannot prove.
export const fanClubMemberships = pgTable(
  "fan_club_memberships",
  {
    fanClubId: uuid("fan_club_id")
      .notNull()
      .references(() => fanClubs.id, { onDelete: "cascade" }),
    memberId: uuid("member_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    notificationsEnabled: boolean("notifications_enabled")
      .notNull()
      .default(true),
    // Preference revocation epoch. No-op/profile updates must not suppress
    // notifications that were already eligible when announced.
    deliveryEpochAt: timestamp("delivery_epoch_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    joinedAt: timestamp("joined_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    primaryKey({ columns: [table.fanClubId, table.memberId] }),
    index("fan_club_memberships_member_joined_idx").on(
      table.memberId,
      table.joinedAt,
    ),
  ],
);

// Reusable author↔guild metadata. Credentials are never stored here: each
// story mapping below owns its independently authorized channel webhook.
export const discordGuildConnections = pgTable(
  "discord_guild_connections",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    guildId: text("guild_id").notNull(),
    guildName: text("guild_name"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    unique("discord_guild_connections_author_guild_unique").on(
      table.authorId,
      table.guildId,
    ),
    index("discord_guild_connections_author_id_idx").on(table.authorId),
    check(
      "discord_guild_connections_guild_id_check",
      sql`case when ${table.guildId} ~ '^[0-9]{1,20}$' then ${table.guildId}::numeric between 1 and 18446744073709551615 else false end`,
    ),
  ],
);

// Story-specific webhook mapping. The token is AES-GCM encrypted with
// storyId:webhookId as AAD; OAuth access/refresh tokens are never retained.
export const fanClubDiscordConnections = pgTable(
  "fan_club_discord_connections",
  {
    fanClubId: uuid("fan_club_id").primaryKey(),
    // Kept on the credential row so a BEFORE DELETE trigger can preserve the
    // AES-GCM AAD context even when the parent story/club is cascading away.
    storyId: uuid("story_id").notNull(),
    guildConnectionId: uuid("guild_connection_id")
      .notNull()
      .references(() => discordGuildConnections.id, { onDelete: "cascade" }),
    webhookId: text("webhook_id").notNull(),
    webhookTokenCiphertext: text("webhook_token_ciphertext").notNull(),
    channelId: text("channel_id").notNull(),
    webhookName: text("webhook_name"),
    // Invitation metadata is locked alongside the webhook so public fan-club
    // SELECT grants can never leak a join link. Validation confirms the invite
    // guild matches guildConnectionId; the invite channel may intentionally
    // differ from the announcement webhook channel.
    discordInviteUrl: text("discord_invite_url"),
    discordInviteCode: text("discord_invite_code"),
    discordInviteChannelId: text("discord_invite_channel_id"),
    discordInviteChannelName: text("discord_invite_channel_name"),
    discordInviteValidatedAt: timestamp("discord_invite_validated_at", {
      withTimezone: true,
    }),
    connectedAt: timestamp("connected_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    foreignKey({
      columns: [table.fanClubId, table.storyId],
      foreignColumns: [fanClubs.id, fanClubs.storyId],
      name: "fan_club_discord_connections_club_story_fk",
    }).onDelete("cascade"),
    unique("fan_club_discord_connections_webhook_unique").on(table.webhookId),
    index("fan_club_discord_connections_guild_idx").on(
      table.guildConnectionId,
    ),
    check(
      "fan_club_discord_connections_webhook_id_check",
      sql`case when ${table.webhookId} ~ '^[0-9]{1,20}$' then ${table.webhookId}::numeric between 1 and 18446744073709551615 else false end`,
    ),
    check(
      "fan_club_discord_connections_channel_id_check",
      sql`case when ${table.channelId} ~ '^[0-9]{1,20}$' then ${table.channelId}::numeric between 1 and 18446744073709551615 else false end`,
    ),
    check(
      "fan_club_discord_connections_invite_channel_id_check",
      sql`${table.discordInviteChannelId} is null or case when ${table.discordInviteChannelId} ~ '^[0-9]{1,20}$' then ${table.discordInviteChannelId}::numeric between 1 and 18446744073709551615 else false end`,
    ),
    check(
      "fan_club_discord_connections_invite_pair_check",
      sql`(${table.discordInviteUrl} is null and ${table.discordInviteCode} is null and ${table.discordInviteValidatedAt} is null)
        or (${table.discordInviteUrl} is not null and ${table.discordInviteCode} is not null and ${table.discordInviteValidatedAt} is not null)`,
    ),
  ],
);

// Provider cleanup must outlive the local connection, club, and story. These
// are immutable snapshots rather than FKs so cascade deletion cannot erase the
// only credential capable of revoking the external Discord webhook.
export const fanClubDiscordRevocationOutbox = pgTable(
  "fan_club_discord_revocation_outbox",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    fanClubId: uuid("fan_club_id").notNull(),
    storyId: uuid("story_id").notNull(),
    webhookId: text("webhook_id").notNull().unique(),
    webhookTokenCiphertext: text("webhook_token_ciphertext").notNull(),
    status: text("status").notNull().default("pending"),
    attempts: integer("attempts").notNull().default(0),
    availableAt: timestamp("available_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    processedAt: timestamp("processed_at", { withTimezone: true }),
    lastError: text("last_error"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    index("fan_club_discord_revocation_outbox_pending_idx").on(
      table.status,
      table.availableAt,
      table.createdAt,
    ),
    check(
      "fan_club_discord_revocation_outbox_webhook_id_check",
      sql`case when ${table.webhookId} ~ '^[0-9]{1,20}$' then ${table.webhookId}::numeric between 1 and 18446744073709551615 else false end`,
    ),
    check(
      "fan_club_discord_revocation_outbox_status_check",
      sql`${table.status} in ('pending', 'processing', 'processed', 'failed')`,
    ),
  ],
);

// Hash-only, DB-backed OAuth state. Atomic consumption prevents callback
// replay across instances; the raw browser state is never stored.
export const discordOAuthStates = pgTable(
  "discord_oauth_states",
  {
    stateHash: text("state_hash").primaryKey(),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    // Snapshot of fan_clubs.discord_connection_generation taken by the OAuth
    // start transaction. Persistence requires this exact value to remain
    // current after the provider exchange finishes.
    connectionGeneration: bigint("connection_generation", { mode: "number" })
      .notNull()
      .default(0),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    consumedAt: timestamp("consumed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    index("discord_oauth_states_expiry_idx").on(table.expiresAt),
    index("discord_oauth_states_author_story_idx").on(
      table.authorId,
      table.storyId,
    ),
    check(
      "discord_oauth_states_connection_generation_check",
      sql`${table.connectionGeneration} >= 0`,
    ),
  ],
);

// Canonical feed items. Chapter IDs remain unique even after retries or
// unpublish/re-publish cycles, so first-publication hooks are idempotent.
export const fanClubAnnouncements = pgTable(
  "fan_club_announcements",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    fanClubId: uuid("fan_club_id")
      .notNull()
      .references(() => fanClubs.id, { onDelete: "cascade" }),
    authorId: uuid("author_id").references(() => profiles.id, {
      onDelete: "set null",
    }),
    type: fanClubAnnouncementTypeEnum("type").notNull(),
    // Snapshot identifier rather than an FK: deleting a chapter must not erase
    // or invalidate the durable first-release announcement/idempotency key.
    chapterId: uuid("chapter_id"),
    title: text("title").notNull(),
    body: text("body").notNull(),
    linkUrl: text("link_url").notNull(),
    publishedAt: timestamp("published_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    unique("fan_club_announcements_id_club_unique").on(
      table.id,
      table.fanClubId,
    ),
    index("fan_club_announcements_club_published_idx").on(
      table.fanClubId,
      table.publishedAt.desc(),
    ),
    uniqueIndex("fan_club_announcements_chapter_unique")
      .on(table.chapterId)
      .where(sql`chapter_id is not null`),
    check(
      "fan_club_announcements_title_length_check",
      sql`char_length(${table.title}) between 1 and 120`,
    ),
    check(
      "fan_club_announcements_body_length_check",
      sql`char_length(${table.body}) between 1 and 1500`,
    ),
    check(
      "fan_club_announcements_shape_check",
      sql`(${table.type} = 'chapter_release' and ${table.chapterId} is not null)
        or (${table.type} = 'author' and ${table.chapterId} is null)`,
    ),
  ],
);

// O(1) publication-time dispatch row. The async worker fans out to the shared
// per-recipient notification_outbox after rechecking current eligibility.
export const fanClubNotificationOutbox = pgTable(
  "fan_club_notification_outbox",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    fanClubId: uuid("fan_club_id")
      .notNull()
      .references(() => fanClubs.id, { onDelete: "cascade" }),
    announcementId: uuid("announcement_id")
      .notNull()
      .unique()
      .references(() => fanClubAnnouncements.id, { onDelete: "cascade" }),
    status: text("status").notNull().default("pending"),
    attempts: integer("attempts").notNull().default(0),
    availableAt: timestamp("available_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    processedAt: timestamp("processed_at", { withTimezone: true }),
    lastError: text("last_error"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    foreignKey({
      columns: [table.announcementId, table.fanClubId],
      foreignColumns: [fanClubAnnouncements.id, fanClubAnnouncements.fanClubId],
      name: "fan_club_notification_outbox_announcement_club_fk",
    }).onDelete("cascade"),
    index("fan_club_notification_outbox_pending_idx").on(
      table.status,
      table.availableAt,
      table.createdAt,
    ),
    index("fan_club_notification_outbox_club_status_idx").on(
      table.fanClubId,
      table.status,
    ),
    check(
      "fan_club_notification_outbox_status_check",
      sql`${table.status} in ('pending', 'processing', 'processed', 'failed')`,
    ),
  ],
);

// Independent Discord queue. It resolves the current story mapping at send
// time, so disconnect, archive, pause, or ownership transfer fails closed.
export const fanClubDiscordOutbox = pgTable(
  "fan_club_discord_outbox",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    fanClubId: uuid("fan_club_id")
      .notNull()
      .references(() => fanClubs.id, { onDelete: "cascade" }),
    announcementId: uuid("announcement_id")
      .notNull()
      .unique()
      .references(() => fanClubAnnouncements.id, { onDelete: "cascade" }),
    status: text("status").notNull().default("pending"),
    attempts: integer("attempts").notNull().default(0),
    availableAt: timestamp("available_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    processedAt: timestamp("processed_at", { withTimezone: true }),
    providerMessageId: text("provider_message_id"),
    lastError: text("last_error"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    foreignKey({
      columns: [table.announcementId, table.fanClubId],
      foreignColumns: [fanClubAnnouncements.id, fanClubAnnouncements.fanClubId],
      name: "fan_club_discord_outbox_announcement_club_fk",
    }).onDelete("cascade"),
    index("fan_club_discord_outbox_pending_idx").on(
      table.status,
      table.availableAt,
      table.createdAt,
    ),
    index("fan_club_discord_outbox_club_status_idx").on(
      table.fanClubId,
      table.status,
    ),
    check(
      "fan_club_discord_outbox_status_check",
      sql`${table.status} in ('pending', 'processing', 'processed', 'failed')`,
    ),
  ],
);

// ─── Wiki ───────────────────────────────────────

export const wikis = pgTable("wikis", {
  id: uuid("id").primaryKey().defaultRandom(),
  storyId: uuid("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date()),
},
  (table) => [
    index("wikis_story_id_idx").on(table.storyId),
  ]
);

export const wikiCategories = pgTable("wiki_categories", {
  id: uuid("id").primaryKey().defaultRandom(),
  wikiId: uuid("wiki_id")
    .notNull()
    .references(() => wikis.id, { onDelete: "cascade" }),
  category: wikiCategoryEnum("category").notNull(),
  label: text("label").notNull(),
  // NULL = "not placed yet" — the canvas auto-places the zone once (below/right
  // of the others) and persists it. NO default(0): 0,0 is a real spot, so a
  // default would look like a saved position and stack zones at the origin.
  canvasX: doublePrecision("canvas_x"),
  canvasY: doublePrecision("canvas_y"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const wikiEntries = pgTable("wiki_entries", {
  id: uuid("id").primaryKey().defaultRandom(),
  categoryId: uuid("category_id")
    .notNull()
    .references(() => wikiCategories.id, { onDelete: "cascade" }),
  parentEntryId: uuid("parent_entry_id")
    .references((): AnyPgColumn => wikiEntries.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  description: text("description"),
  content: jsonb("content"),  // Tiptap rich-text JSON
  // Bulk Wiki generation provenance. The stable entity key lets regeneration
  // find a page even after the author renames it; the narrative hash lets the
  // runner distinguish untouched generated copy/category from a direct Wiki edit.
  aiGeneratedEntityKey: text("ai_generated_entity_key"),
  aiGeneratedContentHash: text("ai_generated_content_hash"),
  data: jsonb("data").notNull().default({}),
  imageUrl: text("image_url"),
  // "Pages" ordering — a row with parentEntryId set is a Page (another document
  // about the same entry, e.g. "Backstory"). pageOrder sorts pages within their
  // parent entry; top-level entries keep the default 0.
  pageOrder: integer("page_order").notNull().default(0),
  // Public wiki — the author opts each entry in individually (default off, so
  // spoilers never leak). `slug` is generated on first publish and is unique
  // per world (one world per story, so this is effectively per-story).
  isPublic: boolean("is_public").notNull().default(false),
  slug: text("slug"),
  // Canvas position inside the entry's category zone. NULL = "unplaced" — the
  // canvas grid-places the card. (0,0) is a REAL saved position (zone origin),
  // which is why there is deliberately no default: new rows start unplaced.
  canvasX: doublePrecision("canvas_x"),
  canvasY: doublePrecision("canvas_y"),
  // AI provenance — null means human-created. Set when the assistant creates
  // the row via apply-with-undo. Cleared when the author dismisses the badge.
  aiCreatedAt: timestamp("ai_created_at", { withTimezone: true }),
  aiConversationId: uuid("ai_conversation_id")
    .references((): AnyPgColumn => assistantConversations.id, { onDelete: "set null" }),
  aiMessageId: uuid("ai_message_id")
    .references((): AnyPgColumn => assistantMessages.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date()),
});

export const wikiConnections = pgTable(
  "wiki_connections",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sourceEntryId: uuid("source_entry_id")
      .notNull()
      .references(() => wikiEntries.id, { onDelete: "cascade" }),
    targetEntryId: uuid("target_entry_id")
      .notNull()
      .references(() => wikiEntries.id, { onDelete: "cascade" }),
    relationLabel: text("relation_label").notNull(),
    aiCreatedAt: timestamp("ai_created_at", { withTimezone: true }),
    aiConversationId: uuid("ai_conversation_id")
      .references((): AnyPgColumn => assistantConversations.id, { onDelete: "set null" }),
    aiMessageId: uuid("ai_message_id")
      .references((): AnyPgColumn => assistantMessages.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => [
    unique("unique_connection").on(table.sourceEntryId, table.targetEntryId),
  ]
);

// ─── Wiki Generation Jobs ───────────────────────────────
// Backs the background "Generate Wiki from Chapters" task. The trigger inserts
// a row and runs the AI extraction via Next's after(); the row is the single
// source of truth the global progress tray polls, so it survives navigation and
// reload. authorId is denormalized for cheap per-author tray queries.
export const wikiGenerationJobs = pgTable(
  "wiki_generation_jobs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    wikiId: uuid("wiki_id")
      .notNull()
      .references(() => wikis.id, { onDelete: "cascade" }),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    status: wikiGenerationStatusEnum("status").notNull().default("queued"),
    stats: jsonb("stats"), // GenerateWikiStats snapshot, updated as it progresses
    error: text("error"),
    startedAt: timestamp("started_at", { withTimezone: true }),
    finishedAt: timestamp("finished_at", { withTimezone: true }),
    dismissedAt: timestamp("dismissed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    index("wiki_generation_jobs_author_idx").on(table.authorId),
    index("wiki_generation_jobs_world_idx").on(table.wikiId),
    index("wiki_generation_jobs_story_id_idx").on(table.storyId),
  ]
);

// ─── Chapter Import Jobs ─────────────────────────────────
// Backs the background bulk-import task (.md / .docx → chapters). The author
// uploads raw source files to R2, the trigger inserts a row + manifest of R2
// keys and runs conversion via Next's after(); the row is the single source of
// truth the global progress tray polls, so the import survives navigation and
// reload. The runner converts + inserts one source file per transaction and
// advances filesProcessed/chaptersCreated, making a resumed run idempotent.
// authorId is denormalized for cheap per-author tray queries.
export const chapterImportJobs = pgTable(
  "chapter_import_jobs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    status: chapterImportStatusEnum("status").notNull().default("queued"),
    // [{ key, name, kind }] — R2 object keys for the uploaded source files, in
    // import order.
    manifest: jsonb("manifest").notNull(),
    // null = one source file per chapter; 1 or 2 = split a single document on
    // headings of that level into many chapters.
    splitLevel: integer("split_level"),
    published: boolean("published").notNull().default(false),
    // Imported chapters are appended after existing ones: sortOrder = this + n.
    startSortOrder: integer("start_sort_order").notNull().default(0),
    totalFiles: integer("total_files").notNull(),
    filesProcessed: integer("files_processed").notNull().default(0),
    chaptersCreated: integer("chapters_created").notNull().default(0),
    // [{ name, message }] — per-file conversion failures that didn't abort the run.
    errors: jsonb("errors").notNull().default([]),
    error: text("error"), // terminal failure message
    startedAt: timestamp("started_at", { withTimezone: true }),
    finishedAt: timestamp("finished_at", { withTimezone: true }),
    dismissedAt: timestamp("dismissed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    index("chapter_import_jobs_author_idx").on(table.authorId),
    index("chapter_import_jobs_story_idx").on(table.storyId),
    index("chapter_import_jobs_status_idx").on(table.status),
  ]
);

// Marketing posts published to social platforms (TikTok / Instagram) from the
// admin social tool. One row per platform per submit — a single admin submit
// targeting both platforms creates two rows so each can fail/succeed and be
// polled independently.
export const socialPosts = pgTable(
  "social_posts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    createdBy: uuid("created_by")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    platform: socialPlatformEnum("platform").notNull(),
    kind: socialPostKindEnum("kind").notNull(),
    caption: text("caption").notNull().default(""),
    // R2 object keys under the social/ prefix, in slide order for carousels.
    mediaKeys: jsonb("media_keys").$type<string[]>().notNull(),
    status: socialPostStatusEnum("status").notNull().default("processing"),
    // TikTok publish_id, or Instagram published media id once live.
    externalId: text("external_id"),
    // Instagram container id — kept so a publish that timed out while the
    // container was still processing can be retried without re-uploading.
    igCreationId: text("ig_creation_id"),
    permalink: text("permalink"),
    error: text("error"),
    publishedAt: timestamp("published_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    index("social_posts_status_idx").on(table.status),
    index("social_posts_created_at_idx").on(table.createdAt),
    index("social_posts_created_by_idx").on(table.createdBy),
  ]
);

// Instagram marketing assets prepared by an admin for a specific author.
// Unlike social_posts, these files are retained as a private author-facing
// library instead of being deleted after a platform dispatch completes.
export const authorInstagramPosts = pgTable(
  "author_instagram_posts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    uploadedBy: uuid("uploaded_by")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    kind: socialPostKindEnum("kind").notNull(),
    caption: text("caption").notNull().default(""),
    instagramUrl: text("instagram_url"),
    // R2 keys under author-instagram/<authorId>/, in carousel order.
    mediaKeys: jsonb("media_keys").$type<string[]>().notNull(),
    reviewStatus: authorInstagramReviewStatusEnum("review_status")
      .notNull()
      .default("pending"),
    authorFeedback: text("author_feedback"),
    reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
    archivedAt: timestamp("archived_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    index("author_instagram_posts_author_created_idx").on(
      table.authorId,
      table.createdAt
    ),
    index("author_instagram_posts_created_idx").on(
      table.createdAt,
      table.id
    ),
    index("author_instagram_posts_status_created_idx").on(
      table.reviewStatus,
      table.createdAt,
      table.id
    ),
    index("author_instagram_posts_author_status_created_idx").on(
      table.authorId,
      table.reviewStatus,
      table.createdAt,
      table.id
    ),
    index("author_instagram_posts_active_created_idx")
      .on(
        table.createdAt,
        table.id
      )
      .where(sql`${table.archivedAt} is null`),
    index("author_instagram_posts_archived_created_idx")
      .on(
      table.createdAt,
      table.id
      )
      .where(sql`${table.archivedAt} is not null`),
    index("author_instagram_posts_uploaded_by_idx").on(table.uploadedBy),
  ]
);

// Two-way discussion attached to an author Instagram approval. The sender flag
// is captured at write time because an admin can also have author capability;
// it represents which side of this review thread they spoke for.
export const authorInstagramPostComments = pgTable(
  "author_instagram_post_comments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    postId: uuid("post_id")
      .notNull()
      .references(() => authorInstagramPosts.id, { onDelete: "cascade" }),
    senderId: uuid("sender_id").references(() => profiles.id, {
      onDelete: "set null",
    }),
    isAdminReply: boolean("is_admin_reply").notNull().default(false),
    body: text("body").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => [
    index("author_instagram_comments_post_created_idx").on(
      table.postId,
      table.createdAt
    ),
    index("author_instagram_comments_sender_idx").on(table.senderId),
  ]
);

// Durable delivery queue for in-app + email notifications. The primary
// Instagram mutation and this row are committed together; a background worker
// retries delivery without making the user wait on an email provider.
export const notificationOutbox = pgTable(
  "notification_outbox",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    recipientId: uuid("recipient_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    actorId: uuid("actor_id").references(() => profiles.id, {
      onDelete: "set null",
    }),
    type: notificationTypeEnum("type").notNull(),
    title: text("title").notNull(),
    body: text("body").notNull(),
    linkUrl: text("link_url"),
    allowSelf: boolean("allow_self").notNull().default(false),
    emailSubject: text("email_subject").notNull(),
    emailHeading: text("email_heading").notNull(),
    // Optional rich counterpart to the always-plain heading. Keeping it in a
    // separate nullable field preserves durable queue compatibility across
    // rolling deploys, retries, and rollback.
    emailBodyHtml: text("email_body_html"),
    emailSnippet: text("email_snippet"),
    emailCtaLabel: text("email_cta_label").notNull(),
    status: text("status").notNull().default("pending"),
    attempts: integer("attempts").notNull().default(0),
    availableAt: timestamp("available_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    processedAt: timestamp("processed_at", { withTimezone: true }),
    lastError: text("last_error"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    index("notification_outbox_pending_idx").on(
      table.status,
      table.availableAt,
      table.createdAt
    ),
    check(
      "notification_outbox_status_check",
      sql`${table.status} in ('pending', 'processing', 'processed', 'failed')`
    ),
  ]
);

// Tracks presigned uploads before a post record exists. Attached objects are
// retained; abandoned pending objects are deleted by the maintenance sweep.
export const authorInstagramUploads = pgTable(
  "author_instagram_uploads",
  {
    objectKey: text("object_key").primaryKey(),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    uploadedBy: uuid("uploaded_by")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    postId: uuid("post_id").references(() => authorInstagramPosts.id, {
      onDelete: "cascade",
    }),
    status: text("status").notNull().default("pending"),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    attachedAt: timestamp("attached_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    index("author_instagram_uploads_cleanup_idx").on(
      table.status,
      table.expiresAt
    ),
    index("author_instagram_uploads_post_idx").on(table.postId),
    check(
      "author_instagram_uploads_status_check",
      sql`${table.status} in ('pending', 'attached', 'cleaned')`
    ),
  ]
);

export const chapterWikiMentions = pgTable(
  "chapter_wiki_mentions",
  {
    chapterId: uuid("chapter_id")
      .notNull()
      .references(() => chapters.id, { onDelete: "cascade" }),
    wikiEntryId: uuid("wiki_entry_id")
      .notNull()
      .references(() => wikiEntries.id, { onDelete: "cascade" }),
  },
  (table) => [
    primaryKey({ columns: [table.chapterId, table.wikiEntryId] }),
    // chapterId is the PK's leading column; index the other FK.
    index("chapter_wiki_mentions_wiki_entry_id_idx").on(table.wikiEntryId),
  ]
);

// ─── Subscriptions & Paywall (Reader → Author) ──────────

export const subscriptionTiers = pgTable("subscription_tiers", {
  id: uuid("id").primaryKey().defaultRandom(),
  storyId: uuid("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  accessLevel: accessTierEnum("access_level").notNull(),
  priceCents: integer("price_cents").notNull(),
  stripePriceId: text("stripe_price_id"),
  description: text("description"),
  features: text("features").array(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
},
  (table) => [
    index("subscription_tiers_story_id_idx").on(table.storyId),
  ]
);

export const subscriptions = pgTable("subscriptions", {
  id: uuid("id").primaryKey().defaultRandom(),
  readerId: uuid("reader_id")
    .notNull()
    .references(() => profiles.id),
  writerId: uuid("writer_id")
    .notNull()
    .references(() => profiles.id),
  tierId: uuid("tier_id")
    .notNull()
    .references(() => subscriptionTiers.id),
  storyId: uuid("story_id")
    .notNull()
    .references(() => stories.id),
  stripeSubscriptionId: text("stripe_subscription_id"),
  status: text("status").notNull().default("active"),
  costInCoins: integer("cost_in_coins").notNull().default(0),
  nextBillingDate: timestamp("next_billing_date", { withTimezone: true }),
  currentPeriodEnd: timestamp("current_period_end", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
},
  (table) => [
    index("subscriptions_reader_id_idx").on(table.readerId),
    index("subscriptions_writer_id_idx").on(table.writerId),
    index("subscriptions_tier_id_idx").on(table.tierId),
    index("subscriptions_story_id_idx").on(table.storyId),
  ]
);

export const ttsAudioUsage = pgTable(
  "tts_audio_usage",
  {
    readerId: uuid("reader_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    chapterId: uuid("chapter_id")
      .notNull()
      .references(() => chapters.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.readerId, table.storyId] }),
    index("tts_audio_usage_chapter_id_idx").on(table.chapterId),
  ]
);

// ─── Platform Subscriptions (Author → Iloris) ───────────

export const platformSubscriptions = pgTable("platform_subscriptions", {
  id: uuid("id").primaryKey().defaultRandom(),
  authorId: uuid("author_id")
    .notNull()
    .references(() => profiles.id),
  writerTier: writerTierEnum("writer_tier").notNull().default("basic"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  status: text("status").notNull().default("active"),
  currentPeriodEnd: timestamp("current_period_end", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
},
  (table) => [
    index("platform_subscriptions_author_id_idx").on(table.authorId),
  ]
);

// ─── Mature Content Confirmations (Reader → Story) ──────

export const matureContentConfirmations = pgTable(
  "mature_content_confirmations",
  {
    readerId: uuid("reader_id").notNull().references(() => profiles.id, { onDelete: "cascade" }),
    storyId: uuid("story_id").notNull().references(() => stories.id, { onDelete: "cascade" }),
    acceptedAt: timestamp("accepted_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.readerId, table.storyId] }),
    index("mature_content_confirmations_story_id_idx").on(table.storyId),
  ],
).enableRLS();

// ─── Reading Progress (Reader → Story) ───────────────────

export const readingProgress = pgTable(
  "reading_progress",
  {
    readerId: uuid("reader_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    chapterId: uuid("chapter_id")
      .notNull()
      .references(() => chapters.id, { onDelete: "cascade" }),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    primaryKey({ columns: [table.readerId, table.storyId] }),
    // (readerId, storyId) PK covers the reader FK; index the remaining FKs.
    index("reading_progress_reader_recent_idx").on(table.readerId, table.updatedAt),
    index("reading_progress_chapter_id_idx").on(table.chapterId),
    index("reading_progress_story_id_idx").on(table.storyId),
  ]
);

// ─── Reader Completion, Experience, and Spotlights ──────

export const chapterCompletions = pgTable(
  "chapter_completions",
  {
    readerId: uuid("reader_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    chapterId: uuid("chapter_id")
      .notNull()
      .references(() => chapters.id, { onDelete: "cascade" }),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    creditedWordCount: integer("credited_word_count").notNull().default(0),
    completedAt: timestamp("completed_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    primaryKey({ columns: [table.readerId, table.chapterId] }),
    index("chapter_completions_story_reader_idx").on(table.storyId, table.readerId),
    index("chapter_completions_completed_at_idx").on(table.completedAt),
  ],
);

// One durable row per reader/chapter powers author-facing progression without
// retaining every page refresh. Anonymous identities are random browser IDs
// hashed per story before storage and are never exposed to authors.
export const chapterReads = pgTable(
  "chapter_reads",
  {
    id: bigint("id", { mode: "number" }).primaryKey().generatedAlwaysAsIdentity(),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    chapterId: uuid("chapter_id")
      .notNull()
      .references(() => chapters.id, { onDelete: "cascade" }),
    readerId: uuid("reader_id")
      .references(() => profiles.id, { onDelete: "cascade" }),
    anonymousIdHash: text("anonymous_id_hash"),
    firstViewedAt: timestamp("first_viewed_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    lastViewedAt: timestamp("last_viewed_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    completedAt: timestamp("completed_at", { withTimezone: true }),
  },
  (table) => [
    uniqueIndex("chapter_reads_reader_unique")
      .on(table.storyId, table.chapterId, table.readerId)
      .where(sql`${table.readerId} is not null`),
    uniqueIndex("chapter_reads_anonymous_unique")
      .on(table.storyId, table.chapterId, table.anonymousIdHash)
      .where(sql`${table.anonymousIdHash} is not null`),
    index("chapter_reads_reader_recent_idx").on(table.readerId, table.lastViewedAt),
    index("chapter_reads_story_period_idx").on(table.storyId, table.firstViewedAt),
    index("chapter_reads_story_chapter_idx").on(table.storyId, table.chapterId),
    check(
      "chapter_reads_exactly_one_identity_check",
      sql`num_nonnulls(${table.readerId}, ${table.anonymousIdHash}) = 1`,
    ),
  ],
);

export const readerExperienceEvents = pgTable(
  "reader_experience_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    readerId: uuid("reader_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    eventType: text("event_type").notNull(),
    sourceKey: text("source_key").notNull(),
    points: integer("points").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    invalidatedAt: timestamp("invalidated_at", { withTimezone: true }),
  },
  (table) => [
    unique("reader_experience_events_source_unique").on(
      table.readerId,
      table.eventType,
      table.sourceKey,
    ),
    index("reader_experience_events_reader_idx").on(table.readerId, table.createdAt),
    check(
      "reader_experience_events_type_points_check",
      sql`(${table.eventType} = 'chapter_completed' and ${table.points} = 10)
        or (${table.eventType} = 'daily_reading' and ${table.points} = 5)
        or (${table.eventType} = 'story_caught_up' and ${table.points} = 25)`,
    ),
  ],
);

export const readerExperienceTotals = pgTable("reader_experience_totals", {
  readerId: uuid("reader_id")
    .primaryKey()
    .references(() => profiles.id, { onDelete: "cascade" }),
  totalXp: integer("total_xp").notNull().default(0),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
}, (table) => [check("reader_experience_totals_nonnegative_check", sql`${table.totalXp} >= 0`)]);

export const storyReaderQualifications = pgTable(
  "story_reader_qualifications",
  {
    readerId: uuid("reader_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    qualifiedAt: timestamp("qualified_at", { withTimezone: true }).defaultNow().notNull(),
    qualificationReason: text("qualification_reason").notNull(),
    completedChapterCount: integer("completed_chapter_count").notNull(),
    completedWordCount: integer("completed_word_count").notNull(),
    invalidatedAt: timestamp("invalidated_at", { withTimezone: true }),
    invalidatedBy: uuid("invalidated_by")
      .references(() => profiles.id, { onDelete: "set null" }),
    invalidationReason: text("invalidation_reason"),
  },
  (table) => [
    primaryKey({ columns: [table.readerId, table.storyId] }),
    index("story_reader_qualifications_story_qualified_idx").on(table.storyId, table.qualifiedAt),
    check(
      "story_reader_qualifications_reason_check",
      sql`${table.qualificationReason} in ('chapter_count', 'word_count', 'all_available')`,
    ),
  ],
);

export const storySpotlights = pgTable(
  "story_spotlights",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    readerId: uuid("reader_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    weekStart: date("week_start").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    invalidatedAt: timestamp("invalidated_at", { withTimezone: true }),
  },
  (table) => [
    unique("story_spotlights_reader_story_week_unique").on(
      table.readerId,
      table.storyId,
      table.weekStart,
    ),
    index("story_spotlights_story_created_idx").on(table.storyId, table.createdAt),
    index("story_spotlights_reader_week_idx").on(table.readerId, table.weekStart),
  ],
);

export const spotlightStoryDiscoveryStatuses = pgTable(
  "spotlight_story_discovery_statuses",
  {
    storyId: uuid("story_id")
      .primaryKey()
      .references(() => stories.id, { onDelete: "cascade" }),
    ascendingGraduatedAt: timestamp("ascending_graduated_at", { withTimezone: true }),
    graduatedQualifiedReaders: integer("graduated_qualified_readers"),
    freshFindsGraduatedAt: timestamp("fresh_finds_graduated_at", { withTimezone: true }),
    freshFindsGraduatedQualifiedReaders: integer("fresh_finds_graduated_qualified_readers"),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    check(
      "spotlight_story_discovery_statuses_graduation_check",
      sql`(${table.ascendingGraduatedAt} is null and ${table.graduatedQualifiedReaders} is null)
        or (${table.ascendingGraduatedAt} is not null and ${table.graduatedQualifiedReaders} is not null and ${table.graduatedQualifiedReaders} >= 100)`,
    ),
    check(
      "spotlight_story_statuses_fresh_finds_graduation_check",
      sql`(${table.freshFindsGraduatedAt} is null and ${table.freshFindsGraduatedQualifiedReaders} is null)
        or (${table.freshFindsGraduatedAt} is not null and ${table.freshFindsGraduatedQualifiedReaders} is not null and ${table.freshFindsGraduatedQualifiedReaders} >= 100)`,
    ),
  ],
);

export const spotlightDiscoveryAppearances = pgTable(
  "spotlight_discovery_appearances",
  {
    weekStart: date("week_start").notNull(),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    position: integer("position").notNull(),
    selectionKind: text("selection_kind").notNull(),
    ascendingScore: doublePrecision("ascending_score").notNull(),
    selectedAt: timestamp("selected_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    primaryKey({ columns: [table.weekStart, table.storyId] }),
    unique("spotlight_discovery_appearances_week_position_unique").on(table.weekStart, table.position),
    unique("spotlight_discovery_appearances_week_author_unique").on(table.weekStart, table.authorId),
    index("spotlight_discovery_appearances_story_week_idx").on(table.storyId, table.weekStart),
    check("spotlight_discovery_appearances_position_check", sql`${table.position} between 1 and 25`),
    check("spotlight_discovery_appearances_kind_check", sql`${table.selectionKind} in ('merit', 'rotation')`),
  ],
);

// ─── Bookmarks (Reader → Story) ─────────────────────────

export const bookmarks = pgTable(
  "bookmarks",
  {
    readerId: uuid("reader_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.readerId, table.storyId] }),
    index("bookmarks_reader_recent_idx").on(table.readerId, table.createdAt),
    index("bookmarks_story_id_idx").on(table.storyId),
  ]
);

// ─── Unlocked Chapters (Reader → Chapter) ───────────────

export const unlockedChapters = pgTable(
  "unlocked_chapters",
  {
    userId: uuid("user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    chapterId: uuid("chapter_id")
      .notNull()
      .references(() => chapters.id, { onDelete: "cascade" }),
    unlockedAt: timestamp("unlocked_at", { withTimezone: true }).defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.userId, table.chapterId] }),
    // userId is the PK's leading column; index the chapter FK for reverse lookups.
    index("unlocked_chapters_chapter_id_idx").on(table.chapterId),
  ]
);

// ─── Story Views (Analytics) ─────────────────────────────

// Per-row story views. As of the write-I/O reduction, this table only receives
// LOGGED-IN views (deduped to one row per user/story/chapter/day by
// idx_story_views_dedup_user) so distinct-reader counts stay exact. Anonymous
// views are aggregated into
// story_view_daily instead. The PK is a monotonic bigint identity (not a random
// UUID) so inserts append to the index tail rather than scattering across pages.
export const storyViews = pgTable("story_views", {
  id: bigint("id", { mode: "number" }).primaryKey().generatedAlwaysAsIdentity(),
  storyId: uuid("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  userId: uuid("user_id")
    .references(() => profiles.id, { onDelete: "set null" }),
  sessionId: text("session_id"),
  chapterId: uuid("chapter_id")
    .references(() => chapters.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, table => [index("story_views_user_recent_idx").on(table.userId, table.createdAt)]);

// Aggregated anonymous story views — 16 counter shards per story per UTC day.
// Anonymous reads INCREMENT anon_count here instead of inserting a per-view row into
// story_views, which eliminated the write amplification that made story_views
// the database's single largest WAL generator. Sharding prevents a viral story
// from forcing every anonymous view to lock the same row.
export const storyViewDaily = pgTable(
  "story_view_daily",
  {
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    day: date("day").notNull(),
    shard: integer("shard").notNull().default(0),
    anonCount: integer("anon_count").notNull().default(0),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    primaryKey({ columns: [table.storyId, table.day, table.shard] }),
    check(
      "story_view_daily_shard_check",
      sql`${table.shard} >= 0 AND ${table.shard} < 16`,
    ),
  ]
);

// Aggregate story acquisition by source and UTC day. This deliberately stores
// no session identifier, reader identity, IP, referrer URL, or reading time.
// One row is updated per story/source/day instead of retaining visitor events.
export const storyAcquisitionDaily = pgTable(
  "story_acquisition_daily",
  {
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    day: date("day").notNull(),
    source: text("source").notNull().default("direct"),
    shard: integer("shard").notNull().default(0),
    visits: integer("visits").notNull().default(0),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    primaryKey({ columns: [table.storyId, table.day, table.source, table.shard] }),
    check(
      "story_acquisition_daily_shard_check",
      sql`${table.shard} >= 0 AND ${table.shard} < 16`,
    ),
    check(
      "story_acquisition_daily_visits_check",
      sql`${table.visits} >= 0`,
    ),
    check(
      "story_acquisition_daily_source_length_check",
      sql`char_length(${table.source}) BETWEEN 1 AND 50`,
    ),
    check(
      "story_acquisition_daily_source_check",
      sql`${table.source} in ('direct', 'home', 'browse', 'iloris', 'instagram', 'facebook', 'reddit', 'tiktok', 'x', 'search', 'external')`,
    ),
  ],
);

// ─── Chapter Comments (Reader/Author → Chapter) ─────────

export const chapterComments = pgTable(
  "chapter_comments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    chapterId: uuid("chapter_id")
      .notNull()
      .references(() => chapters.id, { onDelete: "cascade" }),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    // Single-level threading: null for a root comment, else the id of the ROOT
    // comment this reply belongs to (replies-to-replies are flattened onto the
    // root in the create action). Deleting a root cascades its replies.
    parentId: uuid("parent_id").references(
      (): AnyPgColumn => chapterComments.id,
      { onDelete: "cascade" }
    ),
    content: text("content").notNull(),
    moderationHiddenAt: timestamp("moderation_hidden_at", {
      withTimezone: true,
    }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    // List a chapter's comments newest-first.
    index("chapter_comments_chapter_created_idx").on(
      table.chapterId,
      table.createdAt
    ),
    index("chapter_comments_author_id_idx").on(table.authorId),
    // Fetch a root's replies in chronological (oldest-first) order.
    index("chapter_comments_parent_created_idx").on(
      table.parentId,
      table.createdAt
    ),
  ]
);

// Per-comment likes. Composite PK enforces one like per user per comment.
export const commentLikes = pgTable(
  "comment_likes",
  {
    commentId: uuid("comment_id")
      .notNull()
      .references(() => chapterComments.id, { onDelete: "cascade" }),
    userId: uuid("user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.commentId, table.userId] }),
    // commentId is the PK's leading column; index the user FK.
    index("comment_likes_user_id_idx").on(table.userId),
  ]
);

// ─── Chapter Pricing ─────────────────────────────────────

export const chapterPricing = pgTable("chapter_pricing", {
  id: uuid("id").primaryKey().defaultRandom(),
  chapterId: uuid("chapter_id")
    .notNull()
    .unique()
    .references(() => chapters.id, { onDelete: "cascade" }),
  coinPrice: integer("coin_price").notNull(),
  freeAfter: timestamp("free_after", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date()),
});

// ─── Coin System ─────────────────────────────────────────

export const coinWallets = pgTable("coin_wallets", {
  userId: uuid("user_id")
    .primaryKey()
    .references(() => profiles.id, { onDelete: "cascade" }),
  balanceCoins: integer("balance_coins").notNull().default(0),
  balanceCents: integer("balance_cents").notNull().default(0),
  pendingBalanceCoins: integer("pending_balance_coins").notNull().default(0),
  totalPurchased: integer("total_purchased").notNull().default(0),
  totalSpent: integer("total_spent").notNull().default(0),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date()),
});

export const coinTransactions = pgTable("coin_transactions", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id")
    .notNull()
    .references(() => profiles.id, { onDelete: "cascade" }),
  type: coinTransactionTypeEnum("type").notNull(),
  amountCoins: integer("amount_coins").notNull(),
  amountCents: integer("amount_cents"),
  balanceAfter: integer("balance_after").notNull(),
  relatedStoryId: uuid("related_story_id")
    .references(() => stories.id, { onDelete: "set null" }),
  relatedChapterId: uuid("related_chapter_id")
    .references(() => chapters.id, { onDelete: "set null" }),
  stripePaymentId: text("stripe_payment_id").unique(),
  description: text("description"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
},
  (table) => [
    // Read-only indexes for the transaction ledger — no change to ledger logic.
    // (user_id, created_at) is already covered by idx_coin_transactions_user;
    // add covering indexes for the related-entity FKs.
    index("coin_transactions_related_story_id_idx").on(table.relatedStoryId),
    index("coin_transactions_related_chapter_id_idx").on(table.relatedChapterId),
  ]
);

// Cashout reservations survive provider timeouts and are shared by Stripe and PayPal.
export const authorCashouts = pgTable("author_cashouts", {
  id: uuid("id").primaryKey(), // Client request UUID is also the permanent provider idempotency key.
  userId: uuid("user_id").notNull().references(() => profiles.id, { onDelete: "cascade" }),
  method: text("method").$type<"stripe" | "paypal">().notNull(),
  environment: text("environment").notNull(),
  accountKey: text("account_key").notNull(),
  destination: text("destination").notNull(),
  coins: integer("coins").notNull(),
  netCents: integer("net_cents").notNull(),
  status: text("status").$type<"pending" | "succeeded" | "refunded" | "review">().notNull().default("pending"),
  transactionId: uuid("transaction_id").notNull().unique().references(() => coinTransactions.id),
  refundTransactionId: uuid("refund_transaction_id").unique().references(() => coinTransactions.id),
  providerId: text("provider_id"),
  providerItemId: text("provider_item_id"),
  providerStatus: text("provider_status"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  checkedAt: timestamp("checked_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  index("author_cashouts_user_created_idx").on(table.userId, table.createdAt),
  index("author_cashouts_reconcile_idx").on(table.status, table.checkedAt),
  uniqueIndex("author_cashouts_one_active_idx").on(table.userId).where(sql`${table.status} in ('pending', 'review')`),
  uniqueIndex("author_cashouts_provider_idx").on(table.method, table.environment, table.providerId),
  check("author_cashouts_method_check", sql`${table.method} in ('stripe', 'paypal')`),
  check("author_cashouts_status_check", sql`${table.status} in ('pending', 'succeeded', 'refunded', 'review')`),
  check("author_cashouts_amount_check", sql`${table.coins} >= 20 and ${table.coins} <= 1000000 and ${table.netCents} > 0`),
]);

// ─── AI Credit System (author spend — SEPARATE from coins) ──
// Authors spend AI credits on billable AI actions (cover renders, manual
// cover-prompt redrafts). Mirrors the coin ledger invariants: double-entry
// style ledger rows with a balance_after snapshot, denormalized wallet balance
// updated atomically with each transaction, and a unique stripe_payment_id
// idempotency guard. Never write balance_credits outside a ledger function
// (src/lib/ai-credits/ledger.ts).

export const aiCreditWallets = pgTable("ai_credit_wallets", {
  authorId: uuid("author_id")
    .primaryKey()
    .references(() => profiles.id, { onDelete: "cascade" }),
  balanceCredits: integer("balance_credits").notNull().default(0),
  totalGranted: integer("total_granted").notNull().default(0),
  totalPurchased: integer("total_purchased").notNull().default(0),
  totalSpent: integer("total_spent").notNull().default(0),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date()),
});

export const aiCreditTransactions = pgTable(
  "ai_credit_transactions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    type: aiCreditTransactionTypeEnum("type").notNull(),
    // Signed: positive = credit (grant/purchase/refund), negative = spend.
    amountCredits: integer("amount_credits").notNull(),
    // Wallet balance snapshot after this transaction — required for audits.
    balanceAfter: integer("balance_after").notNull(),
    relatedStoryId: uuid("related_story_id").references(() => stories.id, {
      onDelete: "set null",
    }),
    stripePaymentId: text("stripe_payment_id").unique(),
    description: text("description"),
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => [
    index("ai_credit_transactions_author_created_idx").on(
      table.authorId,
      table.createdAt,
    ),
    index("ai_credit_transactions_created_at_idx").on(table.createdAt),
    index("ai_credit_transactions_related_story_id_idx").on(
      table.relatedStoryId,
    ),
  ],
);

// Per-(author, story) free image-generation counters. Cover and Wiki renders
// have independent allowances; both fall through to the same paid wallet.
// `used_count` is the legacy cover counter retained for migration safety.
export const coverFreeUsage = pgTable(
  "cover_free_usage",
  {
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    usedCount: integer("used_count").notNull().default(0),
    wikiUsedCount: integer("wiki_used_count").notNull().default(0),
  },
  (table) => [primaryKey({ columns: [table.authorId, table.storyId] })],
);

// Durable record of an in-flight billable AI action.
//
// Credits are consumed BEFORE the (slow, killable) provider call, so a hard
// serverless termination between the spend and its settlement would otherwise
// strand the author's credits with nothing to reconcile against. Each spend
// opens a `pending` row inside the same transaction as the debit; settlement
// and release both transition out of `pending` in the same transaction as the
// refund they issue. Rows still `pending` past the sweep window are released
// by the reconciliation cron, which is why the status/created_at index exists.
export const aiCreditOperations = pgTable(
  "ai_credit_operations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    storyId: uuid("story_id").references(() => stories.id, {
      onDelete: "set null",
    }),
    reason: aiCreditTransactionTypeEnum("reason").notNull(),
    status: aiCreditOperationStatusEnum("status").notNull().default("pending"),
    // What the spend debited, split the way splitRefund needs to reverse it.
    reservedCredits: integer("reserved_credits").notNull(),
    freeUsed: integer("free_used").notNull().default(0),
    paidUsed: integer("paid_used").notNull().default(0),
    // Credits actually kept once the action finished.
    settledCredits: integer("settled_credits").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    index("ai_credit_operations_status_created_idx").on(
      table.status,
      table.createdAt,
    ),
    index("ai_credit_operations_author_idx").on(table.authorId),
  ],
);

// ─── Plot Threads & Timeline ─────────────────────────────
// NOTE: $onUpdateFn only fires through Drizzle ORM `.update()` calls.
// For full coverage (raw SQL, Supabase dashboard), add a Postgres trigger.

export const plotThreads = pgTable("plot_threads", {
  id: uuid("id").primaryKey().defaultRandom(),
  storyId: uuid("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  parentThreadId: uuid("parent_thread_id")
    .references((): AnyPgColumn => plotThreads.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  colorToken: text("color_token"),
  orderIndex: integer("order_index").default(0),
  aiCreatedAt: timestamp("ai_created_at", { withTimezone: true }),
  aiConversationId: uuid("ai_conversation_id")
    .references((): AnyPgColumn => assistantConversations.id, { onDelete: "set null" }),
  aiMessageId: uuid("ai_message_id")
    .references((): AnyPgColumn => assistantMessages.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date()),
},
  (table) => [
    // story_id already covered by idx_plot_threads_story_id; index the self-ref FK.
    index("plot_threads_parent_thread_id_idx").on(table.parentThreadId),
  ]
);

export const plotEvents = pgTable("plot_events", {
  id: uuid("id").primaryKey().defaultRandom(),
  threadId: uuid("thread_id")
    .notNull()
    .references(() => plotThreads.id, { onDelete: "cascade" }),
  parentEventId: uuid("parent_event_id")
    .references((): AnyPgColumn => plotEvents.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  label: text("label"),
  icon: text("icon"),
  colorToken: text("color_token"),
  description: jsonb("description").notNull().default({}),
  metadata: jsonb("metadata").notNull().default({}),
  orderIndex: integer("order_index").default(0),
  aiCreatedAt: timestamp("ai_created_at", { withTimezone: true }),
  aiConversationId: uuid("ai_conversation_id")
    .references((): AnyPgColumn => assistantConversations.id, { onDelete: "set null" }),
  aiMessageId: uuid("ai_message_id")
    .references((): AnyPgColumn => assistantMessages.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date()),
});

// ─── Contact Submissions ─────────────────────────────────
// Records of contact-form submissions used only for per-IP rate limiting.
// Stores a salted SHA-256 hash of the sender IP, never the raw IP.

export const contactSubmissions = pgTable("contact_submissions", {
  id: uuid("id").primaryKey().defaultRandom(),
  ipHash: text("ip_hash").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// ─── AI Writing Assistant ────────────────────────────────
// Multi-turn conversations between authors and the assistant, scoped per story.
// Tables are created in Milestone A but only populated starting in Milestone B
// (when the streaming endpoint lands).

export const assistantConversations = pgTable("assistant_conversations", {
  id: uuid("id").primaryKey().defaultRandom(),
  storyId: uuid("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  authorId: uuid("author_id")
    .notNull()
    .references(() => profiles.id, { onDelete: "cascade" }),
  title: text("title"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date()),
},
  (table) => [
    // story_id already covered by assistant_conversations_story_idx; index author FK.
    index("assistant_conversations_author_id_idx").on(table.authorId),
  ]
);

export const assistantMessages = pgTable("assistant_messages", {
  id: uuid("id").primaryKey().defaultRandom(),
  conversationId: uuid("conversation_id")
    .notNull()
    .references(() => assistantConversations.id, { onDelete: "cascade" }),
  role: text("role").notNull(),
  content: jsonb("content").notNull(),
  // Closed, prose-free delivery correlation for response feedback. Null means
  // the historical row predates measurable Assistant delivery.
  deliveryMetadata: jsonb("delivery_metadata"),
  deliveredAt: timestamp("delivered_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const assistantResponseFeedback = pgTable(
  "assistant_response_feedback",
  {
    responseId: uuid("response_id")
      .primaryKey()
      .notNull()
      .references(() => assistantMessages.id, { onDelete: "cascade" }),
    contractVersion: text("contract_version").notNull(),
    rating: text("rating").notNull(),
    ratedAt: timestamp("rated_at", { withTimezone: true }).notNull().defaultNow(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    check(
      "assistant_response_feedback_contract_check",
      sql`${table.contractVersion} = 'assistant-feedback.v1'`,
    ),
    check(
      "assistant_response_feedback_rating_check",
      sql`${table.rating} in ('helpful', 'not_helpful')`,
    ),
  ],
);

// Canon-inert Assistant V2 story-record previews. These rows bind one closed,
// typed candidate to an authenticated conversation turn but cannot represent
// applied state. Canonical writes and reversible receipts belong to a later,
// separate mutation journal.
export const assistantV2RecordCandidates = pgTable(
  "assistant_v2_record_candidates",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    conversationId: uuid("conversation_id")
      .notNull()
      .references(() => assistantConversations.id, { onDelete: "cascade" }),
    // Client request UUID validated at the HTTP boundary and reused as the
    // persisted user-message ID, binding retries to exactly one submitted turn.
    requestId: uuid("request_id")
      .notNull()
      .references(() => assistantMessages.id, { onDelete: "cascade" }),
    responseId: uuid("response_id")
      .notNull()
      .references(() => assistantMessages.id, { onDelete: "cascade" }),
    idempotencyHash: text("idempotency_hash").notNull(),
    routeConfidence: doublePrecision("route_confidence").notNull(),
    routeReason: text("route_reason").notNull(),
    // Source IDs are intentionally retained as audit bindings even if the
    // source chapter is later deleted, so this is guarded by the companion
    // scope trigger rather than an on-delete foreign key.
    sourceChapterId: uuid("source_chapter_id"),
    sourceChapterHash: text("source_chapter_hash"),
    evidenceSetHash: text("evidence_set_hash").notNull(),
    candidateVersion: text("candidate_version").notNull(),
    schemaVersion: text("schema_version").notNull(),
    modelVersion: text("model_version").notNull(),
    promptVersion: text("prompt_version").notNull(),
    status: text("status").notNull().default("pending_review"),
    operationCount: integer("operation_count").notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true })
      .notNull()
      .default(sql`now() + interval '7 days'`),
    redactionDueAt: timestamp("redaction_due_at", { withTimezone: true })
      .notNull()
      .default(sql`now() + interval '7 days'`),
    redactedAt: timestamp("redacted_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    uniqueIndex("assistant_v2_record_candidates_response_idx").on(
      table.responseId,
    ),
    uniqueIndex("assistant_v2_record_candidates_idempotency_idx").on(
      table.idempotencyHash,
    ),
    uniqueIndex("assistant_v2_record_candidates_request_idx").on(table.requestId),
    index("assistant_v2_record_candidates_conversation_created_idx").on(
      table.conversationId,
      table.createdAt,
    ),
    index("assistant_v2_record_candidates_cleanup_idx").on(
      table.status,
      table.redactionDueAt,
    ),
    check(
      "assistant_v2_record_candidates_idempotency_hash_check",
      sql`${table.idempotencyHash} ~ '^[0-9a-f]{64}$'`,
    ),
    check(
      "assistant_v2_record_candidates_route_confidence_check",
      sql`${table.routeConfidence} between 0 and 1`,
    ),
    check(
      "assistant_v2_record_candidates_route_reason_check",
      sql`char_length(${table.routeReason}) between 1 and 200`,
    ),
    check(
      "assistant_v2_record_candidates_source_check",
      sql`(
        ${table.sourceChapterId} is null
        and ${table.sourceChapterHash} is null
      ) or (
        ${table.sourceChapterId} is not null
        and ${table.sourceChapterHash} ~ '^[0-9a-f]{64}$'
      )`,
    ),
    check(
      "assistant_v2_record_candidates_evidence_hash_check",
      sql`${table.evidenceSetHash} ~ '^[0-9a-f]{64}$'`,
    ),
    check(
      "assistant_v2_record_candidates_versions_check",
      sql`char_length(${table.candidateVersion}) between 1 and 100
        and char_length(${table.schemaVersion}) between 1 and 100
        and char_length(${table.modelVersion}) between 1 and 100
        and char_length(${table.promptVersion}) between 1 and 100`,
    ),
    check(
      "assistant_v2_record_candidates_status_check",
      sql`${table.status} in ('pending_review', 'provisional', 'rejected', 'expired', 'redacted')`,
    ),
    check(
      "assistant_v2_record_candidates_operation_count_check",
      sql`${table.operationCount} between 1 and 10`,
    ),
    check(
      "assistant_v2_record_candidates_retention_check",
      sql`${table.expiresAt} >= ${table.createdAt}
        and ${table.redactionDueAt} = ${table.expiresAt}
        and (
          (${table.status} = 'redacted' and ${table.redactedAt} is not null)
          or (${table.status} <> 'redacted' and ${table.redactedAt} is null)
        )`,
    ),
  ],
);

export const assistantV2CandidateOperations = pgTable(
  "assistant_v2_candidate_operations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    candidateId: uuid("candidate_id")
      .notNull()
      .references(() => assistantV2RecordCandidates.id, { onDelete: "cascade" }),
    position: integer("position").notNull(),
    kind: text("kind").notNull(),
    // Null is reserved for retention redaction. The payload hash and all
    // source/target bindings remain for idempotency and audit.
    payload: jsonb("payload"),
    // Server-derived before-values for update fields. Redacted with payload.
    previousPayload: jsonb("previous_payload"),
    payloadHash: text("payload_hash").notNull(),
    evidenceBindings: jsonb("evidence_bindings").notNull(),
    targetKind: text("target_kind"),
    targetId: uuid("target_id"),
    expectedTargetHash: text("expected_target_hash"),
    // Snapshot the label shown when the candidate was generated. Target rows
    // remain mutable/deletable; this private plaintext is removed at retention.
    targetLabel: text("target_label"),
    dependencyBindings: jsonb("dependency_bindings")
      .notNull()
      .default(sql`'[]'::jsonb`),
    verificationStatus: text("verification_status").notNull(),
    verificationIssues: jsonb("verification_issues")
      .notNull()
      .default(sql`'[]'::jsonb`),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    uniqueIndex("assistant_v2_candidate_operations_position_idx").on(
      table.candidateId,
      table.position,
    ),
    check(
      "assistant_v2_candidate_operations_position_check",
      sql`${table.position} between 1 and 10`,
    ),
    check(
      "assistant_v2_candidate_operations_kind_check",
      sql`${table.kind} in ('wiki_entry_create', 'wiki_entry_update', 'wiki_connection_create', 'plot_thread_create', 'plot_thread_update', 'plot_event_create', 'plot_event_update')`,
    ),
    check(
      "assistant_v2_candidate_operations_payload_check",
      sql`${table.payload} is null or (
        jsonb_typeof(${table.payload}) = 'object'
        and octet_length(${table.payload}::text) between 2 and 65536
      )`,
    ),
    check(
      "assistant_v2_candidate_operations_payload_hash_check",
      sql`${table.payloadHash} ~ '^[0-9a-f]{64}$'`,
    ),
    check(
      "assistant_v2_candidate_operations_previous_payload_check",
      sql`${table.previousPayload} is null or (
        jsonb_typeof(${table.previousPayload}) = 'object'
        and octet_length(${table.previousPayload}::text) between 2 and 65536
      )`,
    ),
    check(
      "assistant_v2_candidate_operations_evidence_check",
      sql`case
        when jsonb_typeof(${table.evidenceBindings}) = 'array'
        then jsonb_array_length(${table.evidenceBindings}) between 1 and 12
          and octet_length(${table.evidenceBindings}::text) <= 65536
        else false
      end`,
    ),
    check(
      "assistant_v2_candidate_operations_target_check",
      sql`(
        ${table.targetKind} is null
        and ${table.targetId} is null
        and ${table.expectedTargetHash} is null
        and ${table.targetLabel} is null
      ) or (
        ${table.targetKind} in ('wiki_entry', 'wiki_connection', 'plot_thread', 'plot_event')
        and ${table.targetId} is not null
        and ${table.expectedTargetHash} ~ '^[0-9a-f]{64}$'
        and (
          (${table.targetLabel} is not null
            and char_length(${table.targetLabel}) between 1 and 240
            and btrim(${table.targetLabel}) <> '')
          or (${table.targetLabel} is null and ${table.payload} is null)
        )
      )`,
    ),
    check(
      "assistant_v2_candidate_operations_dependencies_check",
      sql`case
        when jsonb_typeof(${table.dependencyBindings}) = 'array'
        then jsonb_array_length(${table.dependencyBindings}) between 0 and 2
          and octet_length(${table.dependencyBindings}::text) <= 32768
        else false
      end`,
    ),
    check(
      "assistant_v2_candidate_operations_verification_status_check",
      sql`${table.verificationStatus} in ('verified', 'provisional', 'rejected')`,
    ),
    check(
      "assistant_v2_candidate_operations_verification_issues_check",
      sql`case
        when jsonb_typeof(${table.verificationIssues}) = 'array'
        then jsonb_array_length(${table.verificationIssues}) between 0 and 7
          and octet_length(${table.verificationIssues}::text) <= 16384
        else false
      end`,
    ),
  ],
);

// Server-managed receipt for one deterministic, character-exact editor
// transfer. Raw request and chapter prose never enter this table. The nullable
// acknowledgement binding is populated only after the mounted editor reports
// acceptance of the exact transfer hash. The runtime feature was retired in
// Assistant V2 PR 5; this declaration remains because the migration was already
// applied and Drizzle must continue to represent the production schema.
export const assistantExactTransferReceipts = pgTable(
  "assistant_exact_transfer_receipts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    idempotencyKey: uuid("idempotency_key").notNull(),
    conversationId: uuid("conversation_id")
      .notNull()
      .references(() => assistantConversations.id, { onDelete: "cascade" }),
    requestMessageId: uuid("request_message_id")
      .notNull()
      .references(() => assistantMessages.id, { onDelete: "cascade" }),
    acknowledgementMessageId: uuid("acknowledgement_message_id").references(
      () => assistantMessages.id,
      { onDelete: "cascade" },
    ),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    chapterId: uuid("chapter_id")
      .notNull()
      .references(() => chapters.id, { onDelete: "cascade" }),
    authorId: uuid("author_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    transferHash: text("transfer_hash").notNull(),
    expectedChapterHash: text("expected_chapter_hash").notNull(),
    transferCharacterCount: integer("transfer_character_count").notNull(),
    status: text("status").notNull().default("pending"),
    acceptedAt: timestamp("accepted_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    uniqueIndex("assistant_exact_transfer_receipts_idempotency_idx").on(
      table.idempotencyKey,
    ),
    uniqueIndex("assistant_exact_transfer_receipts_request_message_idx").on(
      table.requestMessageId,
    ),
    uniqueIndex("assistant_exact_transfer_receipts_ack_message_idx").on(
      table.acknowledgementMessageId,
    ),
    index("assistant_exact_transfer_receipts_conversation_created_idx").on(
      table.conversationId,
      table.createdAt,
    ),
    check(
      "assistant_exact_transfer_receipts_status_check",
      sql`${table.status} in ('pending', 'accepted')`,
    ),
    check(
      "assistant_exact_transfer_receipts_hashes_check",
      sql`${table.transferHash} ~ '^[0-9a-f]{64}$' and ${table.expectedChapterHash} ~ '^[0-9a-f]{64}$'`,
    ),
    check(
      "assistant_exact_transfer_receipts_character_count_check",
      sql`${table.transferCharacterCount} between 1 and 500000`,
    ),
    check(
      "assistant_exact_transfer_receipts_state_check",
      sql`(
        ${table.status} = 'pending'
        and ${table.acknowledgementMessageId} is null
        and ${table.acceptedAt} is null
      ) or (
        ${table.status} = 'accepted'
        and ${table.acknowledgementMessageId} is not null
        and ${table.acceptedAt} is not null
      )`,
    ),
  ],
);

// Durable, bounded memory of the exact chapter version last seen by an
// assistant conversation. One row per conversation/chapter avoids duplicating
// full chapter text on every message while still supporting reliable diffs.
export const assistantChapterContexts = pgTable(
  "assistant_chapter_contexts",
  {
    conversationId: uuid("conversation_id")
      .notNull()
      .references(() => assistantConversations.id, { onDelete: "cascade" }),
    chapterId: uuid("chapter_id")
      .notNull()
      .references(() => chapters.id, { onDelete: "cascade" }),
    contentHash: text("content_hash").notNull(),
    plainText: text("plain_text").notNull(),
    wordCount: integer("word_count").notNull(),
    chapterUpdatedAt: timestamp("chapter_updated_at", { withTimezone: true })
      .notNull(),
    capturedAt: timestamp("captured_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.conversationId, table.chapterId] }),
    index("assistant_chapter_contexts_chapter_id_idx").on(table.chapterId),
    check(
      "assistant_chapter_contexts_word_count_check",
      sql`${table.wordCount} >= 0`,
    ),
  ],
);

export const assistantProposals = pgTable("assistant_proposals", {
  id: uuid("id").primaryKey(),
  conversationId: uuid("conversation_id")
    .notNull()
    .references(() => assistantConversations.id, { onDelete: "cascade" }),
  messageId: uuid("message_id")
    .references(() => assistantMessages.id, { onDelete: "set null" }),
  storyId: uuid("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  authorId: uuid("author_id")
    .notNull()
    .references(() => profiles.id, { onDelete: "cascade" }),
  kind: text("kind").notNull(),
  display: text("display").notNull(),
  payload: jsonb("payload").notNull(),
  status: text("status").notNull().default("pending"),
  appliedAt: timestamp("applied_at", { withTimezone: true }),
  rejectedAt: timestamp("rejected_at", { withTimezone: true }),
  failureMessage: text("failure_message"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .notNull()
    .defaultNow()
    .$onUpdateFn(() => new Date()),
}, (table) => [
  index("assistant_proposals_conversation_created_idx").on(
    table.conversationId,
    table.createdAt,
  ),
  index("assistant_proposals_author_status_idx").on(table.authorId, table.status),
  check(
    "assistant_proposals_status_check",
    sql`${table.status} in ('pending', 'applying', 'applied', 'rejected', 'failed')`,
  ),
  check(
    "assistant_proposals_payload_object_check",
    sql`jsonb_typeof(${table.payload}) = 'object'`,
  ),
]);

// Durable progress for chapter ranges the editor assistant prioritizes for
// semantic indexing. The global background-task tray polls these rows so work
// remains visible across navigation and reloads.
export const assistantIndexJobs = pgTable("assistant_index_jobs", {
  id: uuid("id").primaryKey().defaultRandom(),
  storyId: uuid("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  authorId: uuid("author_id")
    .notNull()
    .references(() => profiles.id, { onDelete: "cascade" }),
  startSortOrder: integer("start_sort_order").notNull(),
  endSortOrder: integer("end_sort_order").notNull(),
  status: text("status").notNull().default("queued"),
  totalChapters: integer("total_chapters").notNull(),
  embeddedChapters: integer("embedded_chapters").notNull().default(0),
  failedChapters: integer("failed_chapters").notNull().default(0),
  error: text("error"),
  startedAt: timestamp("started_at", { withTimezone: true }),
  finishedAt: timestamp("finished_at", { withTimezone: true }),
  dismissedAt: timestamp("dismissed_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .notNull()
    .defaultNow()
    .$onUpdateFn(() => new Date()),
}, (table) => [
  index("assistant_index_jobs_author_created_idx").on(
    table.authorId,
    table.createdAt,
  ),
  index("assistant_index_jobs_story_idx").on(table.storyId),
  uniqueIndex("assistant_index_jobs_active_range_idx")
    .on(table.storyId, table.startSortOrder, table.endSortOrder)
    .where(sql`${table.status} in ('queued', 'running')`),
  check(
    "assistant_index_jobs_status_check",
    sql`${table.status} in ('queued', 'running', 'succeeded', 'failed')`,
  ),
  check(
    "assistant_index_jobs_range_check",
    sql`${table.startSortOrder} > 0 and ${table.endSortOrder} >= ${table.startSortOrder} and ${table.endSortOrder} - ${table.startSortOrder} < 51`,
  ),
  check(
    "assistant_index_jobs_counts_check",
    sql`${table.totalChapters} >= 0 and ${table.embeddedChapters} >= 0 and ${table.failedChapters} >= 0 and ${table.embeddedChapters} + ${table.failedChapters} <= ${table.totalChapters}`,
  ),
]);

// ─── Chapter Summaries (assistant context) ───────────────
// Structured beat-checklist per chapter. Auto-generated by Gemini Flash on
// chapter save. Always loaded into the assistant's cached context.

export const chapterSummaries = pgTable("chapter_summaries", {
  chapterId: uuid("chapter_id")
    .primaryKey()
    .references(() => chapters.id, { onDelete: "cascade" }),
  summary: text("summary").notNull(),
  beats: jsonb("beats").notNull(),
  contentHash: text("content_hash").notNull(),
  generatedAt: timestamp("generated_at", { withTimezone: true }).defaultNow(),
}).enableRLS();

// ─── Chapter Chunks (pgvector RAG) ───────────────────────
// Chunked plain-text prose with 768-dim Gemini embeddings. Indexed via HNSW
// in a hand-written supabase migration (drizzle does not yet emit HNSW DDL).

export const chapterChunks = pgTable(
  "chapter_chunks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    chapterId: uuid("chapter_id")
      .notNull()
      .references(() => chapters.id, { onDelete: "cascade" }),
    storyId: uuid("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    chunkIndex: integer("chunk_index").notNull(),
    text: text("text").notNull(),
    embedding: vector("embedding", { dimensions: 768 }),
    // sha256 of the chapter's tiptap content at embed time. Denormalized across
    // every chunk of a chapter (all rows share one value). Lets the generator
    // short-circuit when re-triggered on unchanged content. Nullable: rows
    // written before this column existed carry NULL and re-embed on next trigger.
    contentHash: text("content_hash"),
  },
  (table) => [
    index("chapter_chunks_text_fts_idx")
      .using("gin", sql`to_tsvector('simple', ${table.text})`),
  ],
).enableRLS();

// ─── Articles ────────────────────────────────────────────
// Editorial/marketing articles authored by the IlorisNovel team (or AI assisted).
// Source of truth is `markdown`; `html` is the cached render produced on write.

export const articles = pgTable("articles", {
  id: uuid("id").primaryKey().defaultRandom(),
  slug: text("slug").notNull().unique(),
  title: text("title").notNull(),
  excerpt: text("excerpt"),
  authorByline: text("author_byline").notNull(),
  bannerUrl: text("banner_url"),
  markdown: text("markdown").notNull(),
  html: text("html").notNull(),
  // Frontmatter fields that don't map to dedicated columns (tags, source URLs,
  // workflow status, target_keyword, etc.). Preserved for provenance.
  metadata: jsonb("metadata")
    .$type<Record<string, unknown>>()
    .notNull()
    .default({}),
  published: boolean("published").notNull().default(false),
  publishedAt: timestamp("published_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .$onUpdateFn(() => new Date()),
});

// ─── Claim Requests ──────────────────────────────────────
// Submitted by would-be claimants of a scraped/seeded author profile.
// Reviewed manually via /admin/claims; on approval, the profile's
// claimed_at is set and the seeded auth user's email is reset to the
// claimant's real address.

export const claimRequests = pgTable(
  "claim_requests",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    profileId: uuid("profile_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    claimantEmail: text("claimant_email").notNull(),
    sourceUrl: text("source_url").notNull(),
    notes: text("notes"),
    status: claimRequestStatusEnum("status").notNull().default("pending"),
    processedBy: uuid("processed_by").references(() => profiles.id, {
      onDelete: "set null",
    }),
    processedAt: timestamp("processed_at", { withTimezone: true }),
    rejectionReason: text("rejection_reason"),
    // One-time takeover token sent in the approval email. We store the SHA-256
    // hash, never the raw token — a leaked DB row can't be used to take over an
    // account. Cleared when the claimant completes takeover or the token expires.
    accessTokenHash: text("access_token_hash"),
    accessTokenExpiresAt: timestamp("access_token_expires_at", {
      withTimezone: true,
    }),
    consumedAt: timestamp("consumed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => [
    index("claim_requests_status_idx").on(table.status, table.createdAt.desc()),
    index("claim_requests_profile_idx").on(table.profileId),
    index("claim_requests_token_hash_idx")
      .on(table.accessTokenHash)
      .where(sql`access_token_hash IS NOT NULL`),
    index("claim_requests_processed_by_idx").on(table.processedBy),
  ],
);

// ─── Password recovery grants ────────────────────────────
// Server-only, one-time records backing the signed recovery cookie.
export const passwordRecoveryGrants = pgTable(
  "password_recovery_grants",
  {
    tokenHash: text("token_hash").primaryKey(),
    userId: uuid("user_id").notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    consumedAt: timestamp("consumed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [index("password_recovery_grants_expires_idx").on(table.expiresAt)],
);

// ─── Notifications ───────────────────────────────────────
// In-app notification feed. One row per delivered notification per recipient.
// Broadcasts (admin_message) fan out to one row per targeted user at send time
// so read state is tracked independently and the read path stays a simple
// recipient-scoped query. All writes go through server actions on the Drizzle
// (RLS-bypassing) connection; RLS only guards the client-side read path.
export const notifications = pgTable(
  "notifications",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // Who receives / sees this notification.
    recipientId: uuid("recipient_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    type: notificationTypeEnum("type").notNull(),
    // Short heading + human-readable body, rendered at send time so the feed
    // needs no per-row joins to display. Deep link is optional.
    title: text("title").notNull(),
    body: text("body").notNull(),
    linkUrl: text("link_url"),
    // Stable identity for one-time platform notices rendered outside the feed
    // (for example, a promotional banner). Unique per recipient when present.
    campaignKey: text("campaign_key"),
    // Who triggered it (commenter / unlocking reader). Null for platform
    // broadcasts. Set null on actor deletion so the notification survives.
    actorId: uuid("actor_id").references(() => profiles.id, {
      onDelete: "set null",
    }),
    storyId: uuid("story_id").references(() => stories.id, {
      onDelete: "cascade",
    }),
    chapterId: uuid("chapter_id").references(() => chapters.id, {
      onDelete: "cascade",
    }),
    // Null = unread. Timestamp = when the recipient marked it read.
    readAt: timestamp("read_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => [
    // Feed query: newest-first for a recipient.
    index("notifications_recipient_created_idx").on(
      table.recipientId,
      table.createdAt.desc()
    ),
    // Unread-count query: only unread rows per recipient.
    index("notifications_recipient_unread_idx")
      .on(table.recipientId)
      .where(sql`read_at IS NULL`),
    uniqueIndex("notifications_recipient_campaign_unique").on(
      table.recipientId,
      table.campaignKey
    ),
    // Covering indexes for the entity FKs (advisor: unindexed_foreign_keys).
    index("notifications_actor_id_idx").on(table.actorId),
    index("notifications_story_id_idx").on(table.storyId),
    index("notifications_chapter_id_idx").on(table.chapterId),
  ]
);

// Internal discovery metadata and a coalescing classification queue. No client grants.
export const storyDiscoveryMetadata = pgTable("story_discovery_metadata", {
  storyId: uuid("story_id").primaryKey().references(() => stories.id, { onDelete: "cascade" }),
  metadata: jsonb("metadata").$type<import("@/lib/recommendations/metadata").StoryClassification>(),
  sourceHash: text("source_hash"),
  model: text("model"),
  classifierVersion: integer("classifier_version").notNull().default(0),
  requestedMilestone: integer("requested_milestone").notNull().default(0),
  completedMilestone: integer("completed_milestone").notNull().default(0),
  requestedVersion: integer("requested_version").notNull().default(1),
  processedVersion: integer("processed_version").notNull().default(0),
  requestedAt: timestamp("requested_at", { withTimezone: true }).notNull().defaultNow(),
  nextAttemptAt: timestamp("next_attempt_at", { withTimezone: true }).notNull().defaultNow(),
  classifiedAt: timestamp("classified_at", { withTimezone: true }),
  leaseId: uuid("lease_id"),
  leaseUntil: timestamp("lease_until", { withTimezone: true }),
  attempts: integer("attempts").notNull().default(0),
  lastError: text("last_error"),
}, table => [
  index("story_discovery_pending_idx").on(table.nextAttemptAt)
    .where(sql`${table.requestedVersion} > ${table.processedVersion}`),
  check("story_discovery_milestones", sql`${table.requestedMilestone} in (0, 1, 20, 70, 100) and ${table.completedMilestone} in (0, 1, 20, 70, 100) and ${table.completedMilestone} <= ${table.requestedMilestone}`),
  check("story_discovery_metadata_size", sql`${table.metadata} is null or octet_length(${table.metadata}::text) <= 4096`),
]);
